import { useState, useEffect, useRef } from "react";
import { useNavigate } from "react-router-dom";
import axios from "axios";
import { ArrowLeft, ArrowRight, CheckCircle, Calculator, Calendar, MapPin, AlertCircle, Clock, Tag, Gift } from "lucide-react";
import { toast } from "sonner";
import ServiceTypeStep from "@/components/instant_estimate/ServiceTypeStep";
import { useHeadlineExperiment } from "@/hooks/useHeadlineExperiment";
import { getAttribution } from "@/utils/attribution";
import { MeshPhoto, meshKindFromName } from "@/components/MeshPhoto";
import { PriceGuarantee } from "@/components/PriceGuarantee";
import { GoogleRatingLine } from "@/components/GoogleRatingLine";
import SuccessStep from "@/components/instant_estimate/SuccessStep";
import { QuoteBreakdown } from "@/components/QuoteBreakdown";

const BACKEND_URL = window.APP_CONFIG?.BACKEND_URL || process.env.REACT_APP_BACKEND_URL || window.location.origin;
const API = `${BACKEND_URL}/api`;

// Generate a unique session ID for funnel tracking
const generateSessionId = () => {
  const stored = sessionStorage.getItem('estimate_session_id');
  if (stored) return stored;
  const newId = `es_${Date.now()}_${Math.random().toString(36).substr(2, 9)}`;
  sessionStorage.setItem('estimate_session_id', newId);
  return newId;
};

// Funnel step names for analytics
const FUNNEL_STEPS = {
  0: "Service Type",
  1: "Window Quantity",
  2: "Window Breakdown",
  3: "Slider Quantity",
  4: "Slider Breakdown",
  5: "Contact Info",
  6: "Address",
  7: "Quote Shown",
  8: "Booking",
  9: "Booked"
};

// Pricing constants
// Default pricing (fallback if API fails) — note: the funnel hard-gates
// step 7 on a successful fetch, so these defaults should never reach the
// customer. They exist for type-stability / initial render only.
const DEFAULT_PRICING = {
  windowRescreen: 50,
  windowNew: 70,
  sliderRescreen: 110,
  sliderNew: 365,
  petScreenUpgrade: 25,
  solarDoor: 50,
  doorDisposal: 10,
  taxRate: 0.0875
};

// Phone number formatter
const formatPhoneNumber = (value) => {
  const phoneNumber = value.replace(/\D/g, '');
  const phoneNumberLength = phoneNumber.length;
  
  if (phoneNumberLength < 4) return phoneNumber;
  if (phoneNumberLength < 7) {
    return `(${phoneNumber.slice(0, 3)}) ${phoneNumber.slice(3)}`;
  }
  return `(${phoneNumber.slice(0, 3)}) ${phoneNumber.slice(3, 6)}-${phoneNumber.slice(6, 10)}`;
};

// Validate phone (10 digits)
const isValidPhone = (phone) => {
  const digits = phone.replace(/\D/g, '');
  return digits.length === 10;
};

// Validate email
const isValidEmail = (email) => {
  const emailRegex = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
  return emailRegex.test(email);
};

export default function InstantEstimate() {
  const navigate = useNavigate();
  // Google review testimonials shown under the "Get My Estimate" CTA on
  // step 5. Rotated every ~7 seconds. Add or reorder freely — the
  // component picks up new entries automatically.
  const REVIEW_TESTIMONIALS = [
    {
      author: "Edward Spilsbury",
      badge: "Local Guide · 81 reviews",
      body:
        "Great service all around. Easy to set up appointment after easy online estimate for the job. Good communication for timely arrival. Brett evaluated the job and the additional screen we asked him to replace. We asked for pet screening and it looks great and effective too. We are very glad we connected with ScreenzZ!",
    },
    {
      author: "Karen Jones",
      badge: "13 reviews",
      body:
        "Brett and Spencer were excellent. They were neat and efficient. The screens look beautiful. We can finally open our windows again! Thank you so much. I highly recommend your service.",
    },
  ];

  const [step, setStep] = useState(0);
  const [reviewIndex, setReviewIndex] = useState(0);
  // Only rotate while the reviews card is visible (step 5). No point
  // spinning a timer on the other 8 steps.
  useEffect(() => {
    if (step !== 5) return;
    const id = setInterval(() => {
      setReviewIndex(i => (i + 1) % REVIEW_TESTIMONIALS.length);
    }, 7000);
    return () => clearInterval(id);
  }, [step]);

  // Fetch business phone once on mount so the "Text us" fallback on the
  // quote card can link out via sms: without hardcoding a number. Silent
  // failure — the fallback just won't render.
  useEffect(() => {
    axios
      .get(`${API}/public-config`)
      .then((res) => setBusinessPhone(res.data?.business_phone || ""))
      .catch(() => {});
  }, []);
  const [loading, setLoading] = useState(false);

  // Scroll to the top of the page whenever the step changes so the user
  // always sees the heading (e.g. "Where should we send your instant
  // quote?") — otherwise the browser retains the previous scroll
  // position and the customer lands mid-form, missing critical context
  // and the reassuring "What's next?" callout at the top.
  useEffect(() => {
    // Reset both window scroll and any scrollable container. Use
    // instant (not smooth) so it feels like a fresh page load, not a
    // jarring scroll animation.
    try {
      window.scrollTo({ top: 0, behavior: "instant" });
      // Some layouts wrap the app in a scroll container — reset those too
      document.documentElement.scrollTop = 0;
      document.body.scrollTop = 0;
    } catch (_e) { /* no-op */ }
  }, [step]);
  const [availableSlots, setAvailableSlots] = useState([]);
  // Client-side cache for the availability batch response. Keyed by ZIP
  // so if the customer taps Back → Forward the times panel repaints
  // instantly instead of re-hitting the API. TTL is intentionally short
  // (60s) because new bookings from other customers can invalidate the
  // slot set at any time.
  const slotsCacheRef = useRef(new Map()); // zip → { at: ms, payload: {...} }
  const SLOTS_CACHE_TTL_MS = 60_000;
  const [leadId, setLeadId] = useState(null);
  const [hasQuestion, setHasQuestion] = useState(false);
  const [resumeData, setResumeData] = useState(null); // For resume flow
  const [businessPhone, setBusinessPhone] = useState("");
  // Booking step (step 8) presentation state — session-only. Kept out of
  // formData because they don't feed the backend; they only drive
  // conversion-optimized UI.
  const [bookingShowQuoteDetails, setBookingShowQuoteDetails] = useState(false);
  const [bookingAddressEdit, setBookingAddressEdit] = useState(false);
  const [bookingSelectedDate, setBookingSelectedDate] = useState(null);
  const [showAppointmentDetails, setShowAppointmentDetails] = useState(false); // Show existing appointment
  const [slotPageIndex, setSlotPageIndex] = useState(0); // Pagination for days (show 2 days at a time)
  const [reservationTimer, setReservationTimer] = useState(null); // Countdown timer in seconds
  const [zipNotServiced, setZipNotServiced] = useState(false); // Track if zip code is not in service area
  // Manual-booking-only ZIP — admin turned self_book off for this area,
  // so the funnel shows a "text us to book" CTA in place of live slots.
  const [manualBookOnly, setManualBookOnly] = useState(false);
  const [slotsChecked, setSlotsChecked] = useState(false); // Track if we've checked for slots
  const [loadingSlots, setLoadingSlots] = useState(false); // Track if we're currently fetching slots
  const reservationTimerRef = useRef(null);
  const addressInputRef = useRef(null);
  const autocompleteRef = useRef(null);
  const bookButtonRef = useRef(null); // For scroll-into-view after slot selection
  const [googleMapsLoaded, setGoogleMapsLoaded] = useState(false);
  const submittedRef = useRef(false); // Prevent double submission
  const [addressStepEnabled, setAddressStepEnabled] = useState(true); // Address step setting
  const [collectEmailEnabled, setCollectEmailEnabled] = useState(false); // Email collection setting
  const [funnelV2Enabled, setFunnelV2Enabled] = useState(false); // V2 simplified condition picker
  const [funnelV3Enabled, setFunnelV3Enabled] = useState(false); // V3 whole-home screen check
  // View-only mode is set from the `view_only=1` URL param sent when the
  // admin chose "Include Estimate Link" but unchecked "Let them book from
  // the link". In this mode the customer sees the quote but the Book Now
  // button + booking-related CTAs are hidden — admin books manually.
  const [viewOnlyMode, setViewOnlyMode] = useState(false);
  const [settingsLoaded, setSettingsLoaded] = useState(false); // true once public settings have been fetched
  const [skippedAddress, setSkippedAddress] = useState(false); // Track if user skipped address
  const [showFlexibleModal, setShowFlexibleModal] = useState(false); // Post-booking "want earlier?" prompt
  const [flexibleSaving, setFlexibleSaving] = useState(false); // Disable buttons while PATCH in flight
  const preEstimateAddressRef = useRef(null); // Ref for address input on pre-estimate step
  const preEstimateAutocompleteRef = useRef(null); // Ref for autocomplete on pre-estimate step
  
  // Dynamic pricing from Items & Pricing settings
  const [pricing, setPricing] = useState(DEFAULT_PRICING);
  // True only after a successful fetch from /api/pricing-items/public.
  // The quote calculation is gated on this — we refuse to show a price
  // built from DEFAULT_PRICING so a stale customer browser / failed API
  // call can never undercut the live price (e.g., a $365 fallback ringing
  // up while the admin has the door priced at $449 in Settings).
  const [pricingLoaded, setPricingLoaded] = useState(false);
  const [pricingError, setPricingError] = useState(false);
  
  // Promotion/Special settings
  const [promotion, setPromotion] = useState(null);
  const [promoCountdown, setPromoCountdown] = useState(null); // { days, hours, minutes, seconds }
  
  // Funnel tracking
  const [funnelSessionId] = useState(() => generateSessionId());
  const headlineExp = useHeadlineExperiment();
  const pageViewSentRef = useRef(false);
  // Resume/deep links (?resume=<leadId>) reopen an existing estimate; they're not new experiment exposures.
  const [isResumeLoad] = useState(() => !!new URLSearchParams(window.location.search).get("resume"));
  const trackedStepsRef = useRef(new Set()); // Track which steps we've already logged "view" for

  const [formData, setFormData] = useState({
    // Service type
    serviceType: "", // "windows", "doors", "both"
    
    // Window screens
    totalWindowScreens: "",
    windowRescreens: "",
    windowNewBuilds: "",
    windowNotSure: "", // V1 "Not sure" stepper — priced as rescreen, frame checked on-site
    windowCondition: "", // V2: "rescreen" | "new" | "not_sure"
    
    // Sliding doors
    totalSliders: "",
    sliderRescreens: "",
    sliderNewBuilds: "",
    sliderNotSure: "",
    sliderCondition: "", // V2: "rescreen" | "new" | "not_sure"
    wantsPetScreen: false,
    petScreenCount: "",
    wantsSolarScreen: false,
    solarScreenCount: "",
    wantsDisposal: false,  // Old door disposal upsell
    // Pre-estimate address (captured before showing estimate)
    preEstimateAddress: "",
    preEstimateCity: "",
    preEstimateState: "",
    preEstimateZip: "",
    
    // Contact
    firstName: "",
    phone: "",
    email: "",
    
    // Address & scheduling
    address: "",
    zipCode: "",
    selectedSlot: null,
    
    // Tracking - ad click IDs
    gclid: "",
    fbclid: "",
    // UTM parameters
    utm_source: "",
    utm_medium: "",
    utm_campaign: "",
    utm_content: "",
    utm_term: "",
    // Referrer tracking
    referrer: "",
    landing_page: ""
  });

  // ---------------------------------------------------------------------
  // Memorial Day Sale 2026 — tier discount + free-door logic
  // ---------------------------------------------------------------------
  // Hardcoded for Phase 1 of the Promotional Funnel System. Will be lifted
  // into a generic `funnels` collection in Phase 2.
  // Rule: applies ONLY to NEW window screens (not rescreens, not slider work).
  //   10–14 → $100 OFF      | 15–19 → $200 OFF      | 20+ → FREE Sliding Door ($426 value)
  // Highest tier earned applies — no stacking.
  const MEMORIAL_DAY_CAMPAIGN = "memorial-day-2026";
  // Sat May 25 2026 11:59 PM PDT  (= 2026-05-26T06:59:59Z)
  const MEMORIAL_DAY_END = new Date("2026-05-26T06:59:59Z");

  const isMemorialDayActive = formData.campaign === MEMORIAL_DAY_CAMPAIGN
    && Date.now() < MEMORIAL_DAY_END.getTime();

  const getMemorialDayTier = (newWindowCount) => {
    if (newWindowCount >= 20) {
      return { tier: 3, label: "FREE Sliding Screen Door", discount: 0, freeDoor: true, doorValue: 426 };
    }
    if (newWindowCount >= 15) {
      return { tier: 2, label: "$200 OFF", discount: 200, freeDoor: false };
    }
    if (newWindowCount >= 10) {
      return { tier: 1, label: "$100 OFF", discount: 100, freeDoor: false };
    }
    return { tier: 0, label: "", discount: 0, freeDoor: false };
  };

  const memorialNextTierAway = (newWindowCount) => {
    if (newWindowCount >= 20) return null; // already top
    if (newWindowCount >= 15) return { needed: 20 - newWindowCount, unlocks: "FREE Sliding Screen Door ($426 Value)", nextQty: 20 };
    if (newWindowCount >= 10) return { needed: 15 - newWindowCount, unlocks: "$200 OFF", nextQty: 15 };
    return { needed: Math.max(0, 10 - newWindowCount), unlocks: "$100 OFF", nextQty: 10 };
  };

  // Capture tracking params on mount and check for resume flow
  useEffect(() => {
    const params = new URLSearchParams(window.location.search);
    const campaignParam = params.get("campaign") || "";

    // Capture all tracking parameters
    setFormData(prev => ({
      ...prev,
      // Promotional campaign (e.g., "memorial-day-2026"). Drives tier-discount
      // logic and lead-source tagging.
      campaign: campaignParam,
      // For Memorial Day, the promo is window-screen-specific and the lander
      // already framed the offer — skip the "What do you need help with?"
      // chooser and lock the funnel to windows.
      ...(campaignParam === MEMORIAL_DAY_CAMPAIGN ? { serviceType: "windows" } : {}),
      // Ad click IDs
      gclid: params.get("gclid") || "",
      fbclid: params.get("fbclid") || "",
      // UTM parameters
      utm_source: params.get("utm_source") || "",
      utm_medium: params.get("utm_medium") || "",
      utm_campaign: params.get("utm_campaign") || "",
      utm_content: params.get("utm_content") || "",
      utm_term: params.get("utm_term") || "",
      // Referrer and landing page
      referrer: document.referrer || "",
      landing_page: window.location.href,
      // Advertorial product preselect — passed via `?pref=petscreen|solar`
      // from an article CTA. We flip the matching upgrade on so the
      // quote funnel starts with the recommendation in place. Users can
      // still untick it; this is only a nudge, not a lock.
      ...(params.get("pref") === "petscreen" ? { wantsPetScreen: true } : {}),
      ...(params.get("pref") === "solar" ? { wantsSolarScreen: true } : {}),
    }));

    // Memorial Day visitors arrive having already chosen the offer on the
    // landing page — jump them straight to the screen-quantity step.
    if (campaignParam === MEMORIAL_DAY_CAMPAIGN) {
      setStep(1);
    }
    
    // Check for resume flow (customer clicking link from email/SMS)
    const resumeLeadId = params.get("resume");
    if (resumeLeadId) {
      // View-only param is only trusted alongside a resume link — random
      // visitors landing on the fresh funnel shouldn't be locked out.
      if (params.get("view_only") === "1") {
        setViewOnlyMode(true);
      }
      checkResumeFlow(resumeLeadId);
    }
    
    // Fetch address step setting
    fetchAddressStepSetting();
    
    // Fetch pricing from Items & Pricing settings
    fetchPricing();
    
    // Fetch promotion settings
    fetchPromotion();
  }, []);

  // Countdown timer effect
  useEffect(() => {
    if (!promotion?.expires_at) {
      setPromoCountdown(null);
      return;
    }

    const updateCountdown = () => {
      const now = new Date();
      const expiry = new Date(promotion.expires_at);
      const diff = expiry - now;

      if (diff <= 0) {
        setPromoCountdown(null);
        setPromotion(null); // Promo expired
        return;
      }

      const days = Math.floor(diff / (1000 * 60 * 60 * 24));
      const hours = Math.floor((diff % (1000 * 60 * 60 * 24)) / (1000 * 60 * 60));
      const minutes = Math.floor((diff % (1000 * 60 * 60)) / (1000 * 60));
      const seconds = Math.floor((diff % (1000 * 60)) / 1000);

      setPromoCountdown({ days, hours, minutes, seconds });
    };

    updateCountdown();
    const interval = setInterval(updateCountdown, 1000);
    return () => clearInterval(interval);
  }, [promotion?.expires_at]);

  // Fetch active promotion
  const fetchPromotion = async () => {
    try {
      const response = await axios.get(`${API}/settings/promotion/public`);
      if (response.data.enabled) {
        setPromotion(response.data);
      }
    } catch (error) {
      // No promotion active - ignore
    }
  };

  // Fetch pricing from backend
  const fetchPricing = async (attempt = 1) => {
    try {
      const response = await axios.get(`${API}/pricing-items/public`, { timeout: 8000 });
      const data = response.data;
      // Backend now emits a `slots` block keyed by canonical funnel slot
      // (window_rescreen, window_new, slider_rescreen, slider_new,
      // petscreen_upgrade, door_disposal). Prefer that over the legacy
      // name-slug lookup so renaming items in Settings doesn't break the
      // funnel. Falls back to the older lookup for backwards-compat with
      // any backend that hasn't shipped the slots block yet.
      const slots = data.slots || {};
      setPricing({
        windowRescreen: slots.window_rescreen?.price
          ?? data.window_screen_rescreen?.price ?? data.window_rescreen?.price
          ?? DEFAULT_PRICING.windowRescreen,
        windowNew: slots.window_new?.price
          ?? data.new_window_screen?.price ?? data.window_new_build?.price ?? data.window_screen_new_build?.price
          ?? DEFAULT_PRICING.windowNew,
        sliderRescreen: slots.slider_rescreen?.price
          ?? data.sliding_door_rescreen?.price ?? data.slider_rescreen?.price
          ?? DEFAULT_PRICING.sliderRescreen,
        sliderNew: slots.slider_new?.price
          ?? data.new_sliding_door_screen?.price ?? data.new_sliding_screen_door?.price ?? data.slider_new_build?.price
          ?? DEFAULT_PRICING.sliderNew,
        petScreenUpgrade: slots.petscreen_upgrade?.price
          ?? data.petscreen_upgrade?.price ?? data.petscreen?.price ?? data.pet_screen?.price
          ?? DEFAULT_PRICING.petScreenUpgrade,
        solarDoor: slots.solar_door?.price
          ?? data.solar_screen_door?.price ?? data.solar_door?.price
          ?? DEFAULT_PRICING.solarDoor,
        doorDisposal: slots.door_disposal?.price
          ?? data.old_door_disposal?.price ?? data.door_disposal?.price
          ?? DEFAULT_PRICING.doorDisposal,
        taxRate: data.tax_rate || DEFAULT_PRICING.taxRate
      });
      setPricingLoaded(true);
      setPricingError(false);
    } catch (error) {
      // Retry with exponential backoff (up to 4 attempts).
      // We DO NOT silently surface DEFAULT_PRICING — that bug shipped a
      // $365 quote for a $449 item once.
      console.error(`Pricing fetch failed (attempt ${attempt})`, error);
      if (attempt < 4) {
        const delayMs = 500 * 2 ** (attempt - 1); // 500, 1000, 2000, 4000
        setTimeout(() => fetchPricing(attempt + 1), delayMs);
      } else {
        setPricingError(true);
      }
    }
  };

  // Fetch address step enabled setting
  const fetchAddressStepSetting = async () => {
    try {
      const [addressRes, emailRes, funnelRes, funnelV3Res] = await Promise.all([
        axios.get(`${API}/settings/address-step/public`),
        axios.get(`${API}/settings/collect-email/public`),
        axios.get(`${API}/settings/estimate-funnel-v2/public`),
        axios.get(`${API}/settings/estimate-funnel-v3/public`)
      ]);
      setAddressStepEnabled(addressRes.data.enabled);
      setCollectEmailEnabled(emailRes.data.enabled);
      // V2 and V3 are mutually exclusive server-side, but guard the client
      // too so an out-of-date fetch order can't leave us with both on.
      const v3 = !!funnelV3Res.data.enabled;
      const v2 = !!funnelRes.data.enabled && !v3;
      setFunnelV2Enabled(v2);
      setFunnelV3Enabled(v3);
    } catch (error) {
      // Defaults if fetch fails
      setAddressStepEnabled(true);
      setCollectEmailEnabled(false);
      setFunnelV2Enabled(false);
      setFunnelV3Enabled(false);
    } finally {
      setSettingsLoaded(true);
    }
  };

  // Which funnel version is live for this visitor? V3 wins over V2, V1 is the default.
  const activeFunnelVersion = funnelV3Enabled ? "v3" : (funnelV2Enabled ? "v2" : "v1");

  // Track funnel event (fire-and-forget, non-blocking)
  const trackFunnelEvent = (eventStep, eventType, metadata = {}) => {
    // First view of this page load = the experiment page_view, whatever step it lands on.
    const pageView = eventType === "view" && !pageViewSentRef.current;
    if (pageView) pageViewSentRef.current = true;
    try {
      axios.post(`${API}/funnel/track`, {
        session_id: funnelSessionId,
        step: eventStep,
        step_name: FUNNEL_STEPS[eventStep] || `Step ${eventStep}`,
        event_type: eventType,
        metadata: { ...(leadId ? { lead_id: leadId } : {}), ...metadata },
        utm_source: formData.utm_source || null,
        utm_medium: formData.utm_medium || null,
        utm_campaign: formData.utm_campaign || null,
        funnel_version: activeFunnelVersion,
        funnel_session_id: funnelSessionId,
        page_view: pageView,
        resumed: isResumeLoad,
        ...headlineExp.eventFields
      }).catch(() => {}); // Silently ignore tracking errors
    } catch (e) {
      // Ignore tracking errors - don't disrupt user flow
    }
  };

  // Track step views (only after settings are loaded so funnel_version is accurate)
  useEffect(() => {
    if (!settingsLoaded || !headlineExp.ready) return;
    // Only track "view" once per step per session
    if (!trackedStepsRef.current.has(step)) {
      trackedStepsRef.current.add(step);
      trackFunnelEvent(step, "view");
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [step, settingsLoaded, headlineExp.ready]);

  // Start 2-minute timer when a slot is selected
  useEffect(() => {
    if (formData.selectedSlot) {
      // Start 2-minute countdown (120 seconds)
      setReservationTimer(120);
      
      // Clear any existing timer
      if (reservationTimerRef.current) {
        clearInterval(reservationTimerRef.current);
      }
      
      // Start countdown
      reservationTimerRef.current = setInterval(() => {
        setReservationTimer(prev => {
          if (prev <= 1) {
            // Time expired - clear selection
            clearInterval(reservationTimerRef.current);
            setFormData(f => ({ ...f, selectedSlot: null }));
            return null;
          }
          return prev - 1;
        });
      }, 1000);
    } else {
      // Clear timer if no slot selected
      if (reservationTimerRef.current) {
        clearInterval(reservationTimerRef.current);
      }
      setReservationTimer(null);
    }
    
    // Cleanup on unmount
    return () => {
      if (reservationTimerRef.current) {
        clearInterval(reservationTimerRef.current);
      }
    };
  }, [formData.selectedSlot?.date, formData.selectedSlot?.time]);

  // When the user lands on step 8, make sure the booking fields are seeded
  // from any prefill source (resume flow, autocomplete, prior step). Without
  // this, a prefilled zip lives only in `preEstimateZip` and the input shows
  // it, but `formData.zipCode` stays empty — so fetchAvailableSlots bails out
  // and "Book Appointment" stays disabled until the user clears + retypes.
  useEffect(() => {
    if (step !== 8) return;

    const updates = {};
    if (!formData.address && formData.preEstimateAddress) {
      updates.address = formData.preEstimateAddress;
    }
    if (!formData.zipCode && formData.preEstimateZip) {
      updates.zipCode = formData.preEstimateZip;
    }
    // Last-resort: extract a 5-digit zip from the address string itself
    // (handles legacy leads where the address contained the zip and no
    // separate zip_code field was stored). We only match when the 5-digit
    // number appears after a US state abbreviation (e.g. ", CA 92596") so
    // we don't accidentally grab a house number.
    if (!updates.zipCode && !formData.zipCode) {
      const src = updates.address || formData.address || formData.preEstimateAddress || "";
      const match = src.match(/\b[A-Z]{2}\s+(\d{5})(?:-\d{4})?\b/i);
      if (match) updates.zipCode = match[1];
    }

    if (Object.keys(updates).length > 0) {
      setFormData(prev => ({ ...prev, ...updates }));
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [step]);

  // Auto-fetch available slots once a valid 5-digit zip is present on step 8.
  // Runs after the prefill effect above applies, and stays idle on re-renders
  // thanks to the slotsChecked / loading / length guards.
  useEffect(() => {
    if (step !== 8) return;
    if ((formData.zipCode || "").length !== 5) return;
    if (availableSlots.length > 0) return;
    if (loadingSlots) return;
    if (slotsChecked) return;
    fetchAvailableSlots();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [step, formData.zipCode]);

  // When the customer picks a time on step 8, gently scroll the Book
  // Appointment button into view. On mobile the button is hidden far below
  // the slot grid, so without this users think nothing happened after tap.
  useEffect(() => {
    if (step !== 8) return;
    if (!formData.selectedSlot) return;
    // Defer one tick so the "Selected: ..." confirmation card renders first,
    // then scroll the button into view with the confirmation pill above it.
    const t = setTimeout(() => {
      if (bookButtonRef.current) {
        bookButtonRef.current.scrollIntoView({ behavior: "smooth", block: "center" });
      }
    }, 120);
    return () => clearTimeout(t);
  }, [step, formData.selectedSlot?.date, formData.selectedSlot?.time]);


  // Format timer as MM:SS
  const formatTimer = (seconds) => {
    if (!seconds) return "";
    const mins = Math.floor(seconds / 60);
    const secs = seconds % 60;
    return `${mins}:${secs.toString().padStart(2, '0')}`;
  };
  
  // Check if customer has already booked or can resume their estimate
  const checkResumeFlow = async (resumeLeadId) => {
    try {
      setLoading(true);
      const response = await axios.get(`${API}/leads/${resumeLeadId}/booking-status`);
      const data = response.data;
      
      if (!data.found) {
        // Lead not found, start fresh
        toast.error("We couldn't find your quote. Please start a new one.");
        return;
      }
      
      if (data.booked) {
        // Already booked - show appointment details
        setResumeData({
          booked: true,
          appointment: data.appointment,
          estimate_data: data.estimate_data
        });
        setShowAppointmentDetails(true);
      } else {
        // Not booked - restore their data and let them continue
        const leadData = data.lead_data;
        setLeadId(resumeLeadId);
        
        // Clean up address - if it's a placeholder value, clear it
        const cleanAddress = (leadData.address && 
          leadData.address.toLowerCase() !== "not provided yet" && 
          leadData.address.toLowerCase() !== "not provided") 
          ? leadData.address 
          : "";
        // "00000" is the sentinel value we write to the lead when an
        // estimate is submitted before the customer enters a real zip.
        // Strip it on resume so the booking step shows an empty zip field
        // (rather than triggering "we don't service your zip code" against
        // the placeholder).
        const cleanZip = (leadData.zip_code && leadData.zip_code !== "00000")
          ? leadData.zip_code
          : "";

        // Restore the granular screen counters from the estimate line items
        // so downstream logic that inspects windowRescreens / sliderNewBuilds
        // / etc (e.g. the 3-screen booking minimum on step 8, or the V3 screen
        // check gate) sees the correct scope. Prior to this the counters
        // stayed at "" on resume → the booking-minimum predicate treated the
        // customer as a "0 screens" order and let them slip past the guard.
        const restoredCounts = {
          windowRescreens: "",
          windowNewBuilds: "",
          windowNotSure: "",
          sliderRescreens: "",
          sliderNewBuilds: "",
          sliderNotSure: "",
          petScreenCount: "",
          solarScreenCount: "",
          wantsPetScreen: false,
          wantsSolarScreen: false,
          wantsDisposal: false,
        };
        for (const item of (leadData.estimate_data?.line_items || [])) {
          const name = (item.name || "").toLowerCase();
          const qty = String(item.quantity || 0);
          // Swinging-door items are a measurement visit, not screens toward the 3-screen minimum.
          if (name.includes("swinging")) continue;
          if (name.includes("frame check")) {
            if (name.includes("window")) restoredCounts.windowNotSure = qty;
            else restoredCounts.sliderNotSure = qty;
          } else if (name.includes("window screen rescreen")) {
            restoredCounts.windowRescreens = qty;
          } else if (name.includes("new window screen")) {
            restoredCounts.windowNewBuilds = qty;
          } else if (name.includes("sliding door rescreen")) {
            restoredCounts.sliderRescreens = qty;
          } else if (name.includes("new sliding screen door")) {
            restoredCounts.sliderNewBuilds = qty;
          } else if (name.includes("petscreen")) {
            restoredCounts.petScreenCount = qty;
            restoredCounts.wantsPetScreen = true;
          } else if (name.includes("solar screen")) {
            restoredCounts.solarScreenCount = qty;
            restoredCounts.wantsSolarScreen = true;
          } else if (name.includes("disposal")) {
            restoredCounts.wantsDisposal = true;
          }
        }

        setFormData(prev => ({
          ...prev,
          firstName: leadData.full_name || "",
          phone: leadData.phone || "",
          email: leadData.email || "",
          address: cleanAddress,
          zipCode: cleanZip,
          ...restoredCounts,
        }));
        
        // Store the estimate data for display - this is CRITICAL for showing the estimate
        setResumeData({
          booked: false,
          estimate_data: leadData.estimate_data
        });
        
        // Jump to estimate display step (step 7) so user can see their estimate
        // and then proceed to booking
        setStep(7);
        toast.success("Welcome back! Here's your quote.");
      }
    } catch (error) {
      console.error("Error checking resume flow:", error);
      // If error, just let them start fresh
    } finally {
      setLoading(false);
    }
  };

  // Initialize Google Maps autocomplete
  useEffect(() => {
    const initAutocomplete = () => {
      if (window.google && window.google.maps && addressInputRef.current && !autocompleteRef.current) {
        autocompleteRef.current = new window.google.maps.places.Autocomplete(addressInputRef.current, {
          componentRestrictions: { country: "us" },
          fields: ["address_components", "formatted_address"],
          types: ["address"]
        });

        autocompleteRef.current.addListener("place_changed", () => {
          const place = autocompleteRef.current.getPlace();
          if (place && place.address_components) {
            let zipCode = "";
            for (const component of place.address_components) {
              if (component.types[0] === "postal_code") {
                zipCode = component.long_name;
              }
            }
            setFormData(prev => ({
              ...prev,
              address: place.formatted_address || prev.address,
              zipCode: zipCode || prev.zipCode
            }));
          }
        });
        setGoogleMapsLoaded(true);
      }
    };

    if (window.googleMapsReady) {
      initAutocomplete();
    } else {
      window.onGoogleMapsReady = initAutocomplete;
    }

    return () => { window.onGoogleMapsReady = null; };
  }, [step]);

  // Initialize Google Maps autocomplete for pre-estimate address step
  useEffect(() => {
    const initPreEstimateAutocomplete = () => {
      if (window.google && window.google.maps && preEstimateAddressRef.current && !preEstimateAutocompleteRef.current && step === 6) {
        preEstimateAutocompleteRef.current = new window.google.maps.places.Autocomplete(preEstimateAddressRef.current, {
          componentRestrictions: { country: "us" },
          fields: ["address_components", "formatted_address"],
          types: ["address"]
        });

        preEstimateAutocompleteRef.current.addListener("place_changed", () => {
          const place = preEstimateAutocompleteRef.current.getPlace();
          if (place && place.address_components) {
            let city = "";
            let state = "";
            let zipCode = "";
            let streetNumber = "";
            let streetName = "";
            
            for (const component of place.address_components) {
              const type = component.types[0];
              if (type === "street_number") streetNumber = component.long_name;
              if (type === "route") streetName = component.long_name;
              if (type === "locality") city = component.long_name;
              if (type === "administrative_area_level_1") state = component.short_name;
              if (type === "postal_code") zipCode = component.long_name;
            }
            
            setFormData(prev => ({
              ...prev,
              preEstimateAddress: place.formatted_address || `${streetNumber} ${streetName}`.trim(),
              preEstimateCity: city,
              preEstimateState: state,
              preEstimateZip: zipCode
            }));
          }
        });
      }
    };

    if (step === 6 && window.googleMapsReady) {
      // Clear previous autocomplete instance
      preEstimateAutocompleteRef.current = null;
      setTimeout(initPreEstimateAutocomplete, 100);
    }
  }, [step]);

  // Calculate estimate with machine-readable line items
  // Business Rules:
  // - NON-TAXABLE (repair): Rescreens use customer's existing frame, materials incidental to labor
  // - TAXABLE (new_build): New physical products sold to customer
  const calculateEstimate = () => {
    const windowRescreens = parseInt(formData.windowRescreens) || 0;
    const windowNewBuilds = parseInt(formData.windowNewBuilds) || 0;
    const sliderRescreens = parseInt(formData.sliderRescreens) || 0;
    const sliderNewBuilds = parseInt(formData.sliderNewBuilds) || 0;
    const windowNotSure = parseInt(formData.windowNotSure) || 0;
    const sliderNotSure = parseInt(formData.sliderNotSure) || 0;
    const petScreenCount = formData.wantsPetScreen ? (parseInt(formData.petScreenCount) || 0) : 0;
    const solarScreenCount = formData.wantsSolarScreen ? (parseInt(formData.solarScreenCount) || 0) : 0;

    // Machine-readable line items structure
    const line_items = [];
    
    // Window Screen Rescreen - NON-TAXABLE (repair service)
    if (windowRescreens > 0) {
      line_items.push({
        name: "Window Screen Rescreen",
        quantity: windowRescreens,
        unit_price: pricing.windowRescreen,
        line_total: windowRescreens * pricing.windowRescreen,
        category: "repair",
        taxable: false
      });
    }

    // "Not sure" windows — priced as rescreen; tech checks the frame on-site
    if (windowNotSure > 0) {
      line_items.push({
        name: "Window Screen Rescreen – Frame Check On-Site",
        quantity: windowNotSure,
        unit_price: pricing.windowRescreen,
        line_total: windowNotSure * pricing.windowRescreen,
        category: "repair",
        taxable: false,
        frame_check: true,
        upgrade_unit_price: pricing.windowNew
      });
    }

    // New Window Screen - TAXABLE (new product)
    if (windowNewBuilds > 0) {
      line_items.push({
        name: "New Window Screen",
        quantity: windowNewBuilds,
        unit_price: pricing.windowNew,
        line_total: windowNewBuilds * pricing.windowNew,
        category: "new_build",
        taxable: true
      });
    }

    // Sliding Door Rescreen - NON-TAXABLE (repair service)
    if (sliderRescreens > 0) {
      line_items.push({
        name: "Sliding Door Rescreen",
        quantity: sliderRescreens,
        unit_price: pricing.sliderRescreen,
        line_total: sliderRescreens * pricing.sliderRescreen,
        category: "repair",
        taxable: false
      });
    }

    if (sliderNotSure > 0) {
      line_items.push({
        name: "Sliding Door Rescreen – Frame Check On-Site",
        quantity: sliderNotSure,
        unit_price: pricing.sliderRescreen,
        line_total: sliderNotSure * pricing.sliderRescreen,
        category: "repair",
        taxable: false,
        frame_check: true,
        upgrade_unit_price: pricing.sliderNew
      });
    }

    // New Sliding Screen Door - TAXABLE (new product)
    if (sliderNewBuilds > 0) {
      line_items.push({
        name: "New Sliding Screen Door",
        quantity: sliderNewBuilds,
        unit_price: pricing.sliderNew,
        line_total: sliderNewBuilds * pricing.sliderNew,
        category: "new_build",
        taxable: true
      });
    }

    // PetScreen Upgrade - NON-TAXABLE (upgrade to existing/new door mesh, not a separate product)
    if (petScreenCount > 0) {
      line_items.push({
        name: "PetScreen Upgrade",
        quantity: petScreenCount,
        unit_price: pricing.petScreenUpgrade,
        line_total: petScreenCount * pricing.petScreenUpgrade,
        category: "repair",
        taxable: false
      });
    }

    // Solar Screen Upgrade — NON-TAXABLE (door mesh upgrade, not a separate
    // product). Mutually exclusive with PetScreen at the funnel UI level.
    if (solarScreenCount > 0) {
      line_items.push({
        name: "Solar Screen Upgrade",
        quantity: solarScreenCount,
        unit_price: pricing.solarDoor,
        line_total: solarScreenCount * pricing.solarDoor,
        category: "repair",
        taxable: false
      });
    }

    // Old Door Disposal - NON-TAXABLE (service)
    // Only applies to new sliding screen doors
    if (formData.wantsDisposal && sliderNewBuilds > 0) {
      line_items.push({
        name: "Old Door Disposal",
        quantity: sliderNewBuilds,
        unit_price: pricing.doorDisposal,
        line_total: sliderNewBuilds * pricing.doorDisposal,
        category: "repair",
        taxable: false
      });
    }

    // Calculate totals
    const subtotal = line_items.reduce((sum, item) => sum + item.line_total, 0);
    const taxable_subtotal = line_items
      .filter(item => item.taxable)
      .reduce((sum, item) => sum + item.line_total, 0);
    const tax = taxable_subtotal * pricing.taxRate;

    // -------- Memorial Day Sale 2026 — auto-apply tier promo --------
    // Triggered by `?campaign=memorial-day-2026`. Counts ONLY new window
    // screens. Highest tier earned applies — no stacking.
    let mdDiscountAmount = 0;
    let mdDiscountType = null;
    let mdDiscountValue = null;
    let mdFreeDoorAdded = false;
    if (isMemorialDayActive) {
      // Tier is based on TOTAL window screens (rescreens + new builds).
      // Mix is allowed — customers can do all rescreens, all new, or any
      // combination and still qualify.
      const totalWindows = windowRescreens + windowNewBuilds + windowNotSure;
      const t = getMemorialDayTier(totalWindows);
      if (t.tier === 3) {
        // Free Sliding Screen Door — add a $0 line item so it appears on
        // the estimate, the email, and any future invoice.
        line_items.push({
          name: "FREE Sliding Screen Door — Memorial Day Sale ($426 Value)",
          quantity: 1,
          unit_price: 0,
          line_total: 0,
          category: "promo",
          taxable: false,
          memorial_day_tier: 3,
        });
        mdFreeDoorAdded = true;
      } else if (t.tier === 1 || t.tier === 2) {
        mdDiscountAmount = t.discount;
        mdDiscountType = "dollar";
        mdDiscountValue = t.discount;
      }
    }

    const total = subtotal - mdDiscountAmount + tax;

    // Return machine-readable estimate structure
    return {
      line_items,
      tax_rate: pricing.taxRate,
      subtotal,
      taxable_subtotal,
      tax,
      // Discount fields — picked up by the customer estimate page, email and SMS
      discount_type: mdDiscountType,
      discount_value: mdDiscountValue,
      discount_amount: mdDiscountAmount,
      subtotal_after_discount: subtotal - mdDiscountAmount,
      // Campaign metadata
      campaign: isMemorialDayActive ? MEMORIAL_DAY_CAMPAIGN : "",
      memorial_day_tier: isMemorialDayActive ? getMemorialDayTier(windowRescreens + windowNewBuilds + windowNotSure).tier : 0,
      memorial_day_free_door: mdFreeDoorAdded,
      total,
      // Legacy format for UI compatibility
      items: line_items.map(item => ({
        label: `${item.name} (${item.quantity})`,
        amount: item.line_total,
        taxable: item.taxable,
        unit_price: item.unit_price,
        quantity: item.quantity,
        upgrade_unit_price: item.upgrade_unit_price
      })),
      taxableAmount: taxable_subtotal
    };
  };

  // Fetch available slots
  const fetchAvailableSlots = async (opts = {}) => {
    if (!formData.zipCode) return;
    if (loadingSlots) return; // Prevent double-clicks

    const forceRefresh = opts.force === true;
    const cacheKey = formData.zipCode;
    const cached = slotsCacheRef.current.get(cacheKey);
    const now = Date.now();

    // Fast path — reuse the last batch response for this ZIP if it's
    // still fresh. Skips both the spinner and the network call so
    // returning to step 8 via Back feels instant.
    if (!forceRefresh && cached && now - cached.at < SLOTS_CACHE_TTL_MS) {
      setZipNotServiced(cached.zipNotServiced);
      setManualBookOnly(!!cached.manualBookOnly);
      setAvailableSlots(cached.slots);
      setSlotsChecked(true);
      setSlotPageIndex(0);
      return;
    }

    // Reset states + show loading indicator immediately
    setLoadingSlots(true);
    setZipNotServiced(false);
    setManualBookOnly(false);
    setSlotsChecked(false);

    try {
      const currentDate = new Date();
      const currentHour = currentDate.getHours();
      const currentMinutes = currentDate.getMinutes();
      const daysWindow = 22;

      const todayStr = (() => {
        const t = new Date();
        return `${t.getFullYear()}-${String(t.getMonth() + 1).padStart(2, '0')}-${String(t.getDate()).padStart(2, '0')}`;
      })();

      // ONE server-side call replaces the old 22 parallel HTTP requests
      // (each of which hit Mongo 5-6 times). Fixes the 15-20s spinner.
      //
      // Also passes `item_name` for every line item on the estimate so the
      // backend can filter out techs that lack a required capability
      // (e.g. a tech who doesn't do new screen doors won't have any slots
      // shown for an estimate that includes a new sliding screen door).
      const slotParams = new URLSearchParams({
        zip_code: formData.zipCode,
        days: String(daysWindow),
      });
      const _liNames = (Array.isArray(estimate?.line_items) ? estimate.line_items : [])
        .map((li) => li?.name)
        .filter(Boolean);
      _liNames.forEach((n) => slotParams.append("item_name", n));
      const resp = await axios
        .get(`${API}/intake/available-slots-batch?${slotParams.toString()}`)
        .catch((err) => {
          console.error("Batch slot fetch failed", err);
          return { data: {} };
        });

      const data = resp?.data || {};

      if (data.message === "no_qualified_tech") {
        setZipNotServiced(true);
        setSlotsChecked(true);
        setAvailableSlots([]);
        slotsCacheRef.current.set(cacheKey, {
          slots: [],
          zipNotServiced: true,
          noQualifiedTech: true,
        });
        setLoadingSlots(false);
        return;
      }

      if (
        data.message === "No service available in this area yet" ||
        data.message === "No techs assigned to this area"
      ) {
        setZipNotServiced(true);
        setSlotsChecked(true);
        setAvailableSlots([]);
        slotsCacheRef.current.set(cacheKey, {
          at: Date.now(),
          zipNotServiced: true,
          manualBookOnly: false,
          slots: [],
        });
        return;
      }

      // ZIP is serviced but self-booking is disabled — show a manual
      // booking CTA instead of the times panel.
      if (data.self_book_enabled === false || data.message === "self_book_disabled") {
        setManualBookOnly(true);
        setSlotsChecked(true);
        setAvailableSlots([]);
        slotsCacheRef.current.set(cacheKey, {
          at: Date.now(),
          zipNotServiced: false,
          manualBookOnly: true,
          slots: [],
        });
        return;
      }

      const rawDays = Array.isArray(data.days) ? data.days : [];

      const slots = [];
      for (const d of rawDays) {
        if (!d?.slots || d.slots.length === 0) continue;

        let availableTimes = d.slots;
        if (d.date === todayStr) {
          availableTimes = availableTimes.filter((slot) => {
            const [hourStr, minuteStr] = (slot.time || '').split(':');
            const slotHour = parseInt(hourStr, 10);
            const slotMinutes = parseInt(minuteStr || '0', 10);
            if (Number.isNaN(slotHour)) return true;
            const currentTotalMinutes = currentHour * 60 + currentMinutes;
            const slotTotalMinutes = slotHour * 60 + slotMinutes;
            return slotTotalMinutes >= currentTotalMinutes + 60;
          });
        }
        if (availableTimes.length === 0) continue;

        const [y, m, day] = d.date.split('-').map((n) => parseInt(n, 10));
        const localDate = new Date(y, m - 1, day);
        const dateLabel = localDate.toLocaleDateString('en-US', {
          weekday: 'short',
          month: 'short',
          day: 'numeric',
        });

        slots.push({
          date: d.date,
          dateLabel,
          times: availableTimes,
          techName: d.tech_name,
          nearbyJobsCount: d.nearby_jobs_count || 0,
          nearestJobMiles: d.nearest_job_miles ?? null,
        });

        if (slots.length >= 7) break;
      }

      slots.sort((a, b) => a.date.localeCompare(b.date));

      setAvailableSlots(slots);
      setSlotsChecked(true);
      setSlotPageIndex(0);
      slotsCacheRef.current.set(cacheKey, {
        at: Date.now(),
        zipNotServiced: false,
        manualBookOnly: false,
        slots,
      });
    } catch (error) {
      console.error("Failed to fetch slots:", error);
      setSlotsChecked(true);
    } finally {
      setLoadingSlots(false);
    }
  };

  // Submit lead
  const submitLead = async (hasQuestionFlag = false) => {
    // Prevent double submission
    if (submittedRef.current || loading) {
      console.log("Preventing double submission");
      return;
    }
    submittedRef.current = true;
    setLoading(true);
    
    try {
      const estimate = calculateEstimate();
      
      // Build human-readable notes
      const notesLines = [
        "INSTANT ESTIMATE REQUEST",
        `Window Rescreens: ${formData.windowRescreens || 0}`,
        `New Windows: ${formData.windowNewBuilds || 0}`,
        `Slider Rescreens: ${formData.sliderRescreens || 0}`,
        `New Sliders: ${formData.sliderNewBuilds || 0}`,
        `Windows Not Sure (frame check): ${formData.windowNotSure || 0}`,
        `Sliders Not Sure (frame check): ${formData.sliderNotSure || 0}`,
        `PetScreen: ${formData.petScreenCount || 0}`,
        `Solar Screen: ${formData.solarScreenCount || 0}`,
        `---`,
        `Subtotal: $${estimate.subtotal.toFixed(2)}`,
        `Taxable Amount: $${estimate.taxable_subtotal.toFixed(2)}`,
        `Tax (8.75%): $${estimate.tax.toFixed(2)}`,
        `Estimated Total: $${estimate.total.toFixed(2)}`,
      ];
      if (hasQuestionFlag) {
        notesLines.push("⚠️ CUSTOMER HAS PRICING QUESTION - FOLLOW UP NEEDED");
      }

      const leadData = {
        service_type: formData.serviceType,
        screen_count: (parseInt(formData.totalWindowScreens) || 0) + (parseInt(formData.totalSliders) || 0),
        screen_type: formData.serviceType,
        full_name: formData.firstName,
        phone: formData.phone,
        email: formData.email,
        address: formData.address || "Not provided yet",
        zip_code: formData.zipCode || "00000",
        notes: notesLines.join("\n"),
        // Machine-readable estimate data for downstream use (invoices, job records, reporting)
        estimate_data: {
          line_items: estimate.line_items,
          tax_rate: estimate.tax_rate,
          subtotal: estimate.subtotal,
          taxable_subtotal: estimate.taxable_subtotal,
          tax: estimate.tax,
          total: estimate.total
        },
        // Ad click tracking
        gclid: formData.gclid || null,
        fbclid: formData.fbclid || null,
        // UTM tracking
        utm_source: formData.utm_source || null,
        utm_medium: formData.utm_medium || null,
        utm_campaign: formData.utm_campaign || null,
        utm_content: formData.utm_content || null,
        utm_term: formData.utm_term || null,
        // Promotional funnel campaign (e.g., "memorial-day-2026")
        campaign: formData.campaign || null,
        source: formData.campaign === MEMORIAL_DAY_CAMPAIGN ? "Memorial Day 2026" : null,
        // Referrer tracking
        referrer: formData.referrer || null,
        landing_page: formData.landing_page || null,
        // SMS consent tracking
        sms_consent: {
          consented: true,
          consent_text_version: "v1.0",
          consent_text: "By submitting this form, you agree that ScreenzZ may contact you by call or text regarding your quote, appointment, or service. Message frequency varies. Message and data rates may apply. Reply STOP to opt out."
        },
        // Flag if customer has a question about pricing
        has_question: hasQuestionFlag,
        // V3 funnel: session id + version for attribution, plus the current
        // screen-check preference (may be null if not yet answered — the
        // book-appointment call updates it later).
        funnel_session_id: funnelSessionId,
        funnel_version: activeFunnelVersion,
        screen_check_preference: formData.screen_check_preference || null,
        frame_check: {
          window_not_sure: parseInt(formData.windowNotSure) || 0,
          slider_not_sure: parseInt(formData.sliderNotSure) || 0,
        },
        ...getAttribution(),
        ...headlineExp.leadFields
      };

      const response = await axios.post(`${API}/intake/submit`, leadData);
      setLeadId(response.data.lead_id);
      
      // Fire FB Pixel event
      if (window.fbq) {
        window.fbq('track', 'Lead', {
          content_name: 'Instant Quote',
          value: estimate.total,
          currency: 'USD'
        });
      }
      
      // Fire GA4 event (flows into Google Ads as "form_submit" conversion)
      if (window.gtag) {
        window.gtag('event', 'form_submit', {
          event_category: 'Instant Quote',
          value: estimate.total,
          currency: 'USD'
        });
      }
      
      return response.data;
    } catch (error) {
      toast.error("Failed to submit. Please try again.");
      submittedRef.current = false; // Reset on error so user can retry
      // Durable record of failed saves (backend logs only keep ~24h).
      trackFunnelEvent(5, "submit_error", {
        status: error.response?.status || null,
        detail: String(error.response?.data?.detail || error.message || "").slice(0, 200),
      });
      throw error;
    } finally {
      setLoading(false);
    }
  };

  // Book appointment - updates existing lead with appointment details
  const bookAppointment = async () => {
    if (!formData.selectedSlot || !leadId) return;
    
    setLoading(true);
    try {
      const estimate = calculateEstimate();
      
      // Use pre-estimate address if main address not filled
      const finalAddress = formData.address || formData.preEstimateAddress;
      const finalZip = formData.zipCode || formData.preEstimateZip;
      
      // Update existing lead with appointment details including selected tech
      await axios.put(`${API}/leads/${leadId}/book`, {
        preferred_date: formData.selectedSlot.date,
        preferred_time: formData.selectedSlot.time,
        address: finalAddress,
        zip_code: finalZip,
        selected_tech_id: formData.selectedSlot.tech_id,
        selected_tech_name: formData.selectedSlot.tech_name,
        // V3 funnel: whole-home screen-check preference (may be null on V1/V2)
        screen_check_preference: formData.screen_check_preference || null
      });

      // Invalidate the client-side availability cache — the slot we just
      // claimed shouldn't be offered again if the customer navigates back
      // to the times panel (or someone else opens a new quote in this same
      // tab). The next fetchAvailableSlots() call will hit the API fresh.
      slotsCacheRef.current.clear();
      
      // Track booking completion in funnel
      trackFunnelEvent(9, "complete", { 
        estimate_total: estimate.total,
        service_type: formData.serviceType
      });
      
      // Fire FB Pixel conversion
      if (window.fbq) {
        window.fbq('track', 'Schedule', {
          content_name: 'Instant Quote Booking',
          value: estimate.total,
          currency: 'USD'
        });
      }
      
      // Fire GA4 event (flows into Google Ads as appointment conversion)
      if (window.gtag) {
        window.gtag('event', 'Instant_Estimate', {
          event_category: 'Appointment Booking',
          value: estimate.total,
          currency: 'USD'
        });
      }
      
      setStep(9); // Success step
      // Surface the "Flexible Schedule" opt-in modal right after success.
      // Small delay so the success screen has time to render underneath.
      setTimeout(() => setShowFlexibleModal(true), 400);
    } catch (error) {
      toast.error("Failed to book. Please try again.");
    } finally {
      setLoading(false);
    }
  };

  // Handle Yes/No on the post-booking "Flexible Schedule" prompt.
  // We always close the modal; we only fire the PATCH on Yes.
  const handleFlexibleResponse = async (wantsFlexible) => {
    setShowFlexibleModal(false);
    if (!wantsFlexible || !leadId) return;
    setFlexibleSaving(true);
    try {
      await axios.patch(`${API}/leads/${leadId}/flexible-schedule`, {
        flexible: true,
      });
      toast.success("Got it — we'll reach out if an earlier slot opens up.");
    } catch (err) {
      // Non-fatal — booking already succeeded. Just log.
      console.error("Failed to set flexible schedule:", err);
    } finally {
      setFlexibleSaving(false);
    }
  };

  // Validation helpers
  const windowBreakdownValid = () => {
    const total = parseInt(formData.totalWindowScreens) || 0;
    const rescreens = parseInt(formData.windowRescreens) || 0;
    const newBuilds = parseInt(formData.windowNewBuilds) || 0;
    return rescreens + newBuilds + (parseInt(formData.windowNotSure) || 0) === total;
  };

  const sliderBreakdownValid = () => {
    const total = parseInt(formData.totalSliders) || 0;
    const rescreens = parseInt(formData.sliderRescreens) || 0;
    const newBuilds = parseInt(formData.sliderNewBuilds) || 0;
    return rescreens + newBuilds + (parseInt(formData.sliderNotSure) || 0) === total;
  };

  // V1 three-way split (Rescreen / New / Not sure). Editing one stepper puts the
  // remainder into Rescreen (or New when Rescreen is edited) so the sum stays = total.
  const splitCounts = (prefix, totalKey, field, val) => {
    const total = parseInt(formData[totalKey]) || 0;
    const keys = { r: `${prefix}Rescreens`, n: `${prefix}NewBuilds`, u: `${prefix}NotSure` };
    const v = Math.min(total, parseInt(val) || 0);
    const next = { r: parseInt(formData[keys.r]) || 0, n: parseInt(formData[keys.n]) || 0, u: parseInt(formData[keys.u]) || 0, [field]: v };
    const fill = field === "r" ? "n" : "r";
    const other = ["r", "n", "u"].find((k) => k !== field && k !== fill);
    if (v + next[other] > total) next[other] = total - v;
    next[fill] = total - v - next[other];
    return { [keys.r]: String(next.r), [keys.n]: String(next.n), [keys.u]: String(next.u) };
  };

  // V2: apply condition selection -> compute rescreens/newBuilds split
  const applyWindowCondition = (condition) => {
    const total = parseInt(formData.totalWindowScreens) || 0;
    let rescreens = 0;
    let newBuilds = 0;
    if (condition === "rescreen") {
      rescreens = total;
    } else if (condition === "new") {
      newBuilds = total;
    } else if (condition === "not_sure") {
      // 50/50 split, round up on new
      newBuilds = Math.ceil(total / 2);
      rescreens = total - newBuilds;
    }
    setFormData({
      ...formData,
      windowCondition: condition,
      windowRescreens: rescreens.toString(),
      windowNewBuilds: newBuilds.toString()
    });
  };

  const applySliderCondition = (condition) => {
    const total = parseInt(formData.totalSliders) || 0;
    let rescreens = 0;
    let newBuilds = 0;
    if (condition === "rescreen") {
      rescreens = total;
    } else if (condition === "new") {
      newBuilds = total;
    } else if (condition === "not_sure") {
      newBuilds = Math.ceil(total / 2);
      rescreens = total - newBuilds;
    }
    setFormData({
      ...formData,
      sliderCondition: condition,
      sliderRescreens: rescreens.toString(),
      sliderNewBuilds: newBuilds.toString(),
      // Reset disposal if no new builds
      wantsDisposal: newBuilds > 0 ? formData.wantsDisposal : false
    });
  };

  // Navigation
  const getNextStep = () => {
    switch (step) {
      case 0: // Service type
        if (formData.serviceType === "windows") return 1;
        if (formData.serviceType === "doors") return 3;
        if (formData.serviceType === "both") return 1;
        return step;
      case 1: // Window quantity
        return 2;
      case 2: // Window breakdown
        if (formData.serviceType === "both") return 3;
        return 5; // Contact
      case 3: // Slider quantity
        return 4;
      case 4: // Slider breakdown + petscreen
        return 5; // Contact
      case 5: // Contact
        // Check if address step is enabled
        if (addressStepEnabled) return 6; // Address step
        return 7; // Skip to Estimate
      case 6: // Address (optional step)
        return 7; // Estimate
      case 7: // Estimate
        return 8; // Booking
      default:
        return step + 1;
    }
  };

  const canProceed = () => {
    switch (step) {
      case 0: return !!formData.serviceType;
      case 1: return parseInt(formData.totalWindowScreens) >= 1;
      case 2: return windowBreakdownValid();
      case 3: return parseInt(formData.totalSliders) > 0;
      case 4: return sliderBreakdownValid();
      case 5: 
        // Email is required (when the collect-email flag is on). Both the
        // value must exist AND pass basic validation before they can move
        // past the contact step.
        const emailValid = !collectEmailEnabled || (formData.email && isValidEmail(formData.email));
        return formData.firstName && isValidPhone(formData.phone) && emailValid;
      case 6: return true; // Address step is always skippable
      case 8: {
        // Address is optional; we only need a zip + selected slot to book.
        // Either the canonical field OR the pre-estimate fallback counts as set.
        const hasZip = (formData.zipCode || formData.preEstimateZip || "").trim().length >= 5;
        return hasZip && !!formData.selectedSlot;
      }
      default: return true;
    }
  };

  const handleNext = async () => {
    // Track step completion
    trackFunnelEvent(step, "complete", { service_type: formData.serviceType });

    // HARD GATE: never let the customer enter the quote page until live
    // prices have been fetched from /api/pricing-items/public. Without
    // this, a slow / failed API call would silently surface the hardcoded
    // DEFAULT_PRICING fallbacks (the $365 sliding-door bug). We retry the
    // fetch and ask the customer to wait a beat.
    const nextStepCandidate = getNextStep();
    if (nextStepCandidate === 7 && !pricingLoaded) {
      if (pricingError) {
        toast.error("We're having trouble loading current prices. Reconnecting…");
        setPricingError(false);
        fetchPricing();
      } else {
        toast.info("Loading current prices…");
      }
      return;
    }

    if (step === 5) {
      // After contact, submit lead. Never show the estimate without a saved lead
      // (submitLead returns nothing when a previous/in-flight submit blocked it).
      const saved = await submitLead(false);
      if (!saved && !leadId) return;
    }
    if (step === 6 && formData.preEstimateAddress) {
      // Update lead with address if provided before estimate
      await updateLeadWithAddress();
    }
    setStep(getNextStep());
  };

  // Update lead with address captured before estimate
  const updateLeadWithAddress = async () => {
    if (!leadId) return;
    
    try {
      await axios.put(`${API}/leads/${leadId}/address`, {
        address: formData.preEstimateAddress || "",
        city: formData.preEstimateCity || "",
        state: formData.preEstimateState || "",
        zip_code: formData.preEstimateZip || ""
      });
    } catch (error) {
      console.error("Failed to update lead with address:", error);
    }
  };

  // Handle skip address step
  const handleSkipAddress = () => {
    // Mirror the handleNext gate — never enter the quote page on stale
    // DEFAULT_PRICING fallbacks.
    if (!pricingLoaded) {
      if (pricingError) {
        toast.error("We're having trouble loading current prices. Reconnecting…");
        setPricingError(false);
        fetchPricing();
      } else {
        toast.info("Loading current prices…");
      }
      return;
    }
    // Track address skip
    trackFunnelEvent(6, "skip");
    setSkippedAddress(true);
    setStep(7); // Go to estimate
  };

  const handleBack = () => {
    // Track going back
    trackFunnelEvent(step, "back");
    
    // Memorial Day visitors enter at step 1 (the chooser was pre-decided on
    // the landing page) — sending them back to step 0 would be confusing. We
    // route them back to the lander instead.
    if (step === 1 && formData.campaign === MEMORIAL_DAY_CAMPAIGN) {
      navigate("/memorial");
      return;
    }
    if (step === 3 && formData.serviceType === "doors") {
      setStep(0);
    } else if (step === 3 && formData.serviceType === "both") {
      setStep(2);
    } else if (step === 5 && formData.serviceType === "doors") {
      setStep(4);
    } else if (step === 5 && formData.serviceType === "windows") {
      setStep(2);
    } else if (step === 5 && formData.serviceType === "both") {
      setStep(4);
    } else if (step === 6) {
      setStep(5); // Back to contact
    } else if (step === 7) {
      // Back from estimate
      if (addressStepEnabled) {
        setStep(6); // Back to address
      } else {
        setStep(5); // Back to contact
      }
    } else if (step === 10) {
      // V3 screen check → back to estimate
      setStep(7);
    } else if (step === 8 && funnelV3Enabled && formData.screen_check_preference) {
      // V3 booking → back to screen-check question, not to the estimate,
      // so the customer can change their mind.
      setStep(10);
    } else {
      setStep(Math.max(0, step - 1));
    }
  };

  const handleQuestionClick = async () => {
    setHasQuestion(true);
    await submitLead(true);
    setStep(9); // Go to thank you with question flag (was 8, now 9)
  };

  // Handle question click for resumed estimates (lead already exists)
  const handleResumeQuestionClick = async () => {
    if (!leadId && !resumeData?.lead_id) {
      toast.error("Unable to process request");
      return;
    }
    
    setLoading(true);
    try {
      // Call backend to send the question response text
      await axios.post(`${API}/leads/${leadId || resumeData?.lead_id}/question`);
      setHasQuestion(true);
      setStep(9); // Go to thank you
      toast.success("We'll text you shortly!");
    } catch (error) {
      console.error("Error sending question:", error);
      toast.error("Failed to send. Please call us instead.");
    } finally {
      setLoading(false);
    }
  };

  const handleAcceptEstimate = () => {
    // If we have pre-estimate address/zip, auto-populate the booking form fields
    const updatedFormData = { ...formData };
    
    if (formData.preEstimateAddress && (!formData.address || formData.address === "Not provided yet")) {
      updatedFormData.address = formData.preEstimateAddress;
    }
    
    if (formData.preEstimateZip && !formData.zipCode) {
      updatedFormData.zipCode = formData.preEstimateZip;
    }
    
    setFormData(updatedFormData);

    // V3 funnel: interject the "While we're there..." screen-check question
    // right before the booking step. Only shown for V3 sessions.
    if (funnelV3Enabled && !formData.screen_check_preference) {
      trackFunnelEvent(10, "view");
      setStep(10); // Screen Check step (V3 only)
      return;
    }

    setStep(8); // Go to booking
    
    // If we have a valid zip code, auto-fetch available slots
    if (updatedFormData.zipCode?.length === 5) {
      setTimeout(() => fetchAvailableSlots(), 200);
    }
  };

  // V3: user answered the Whole-Home Screen Check question.
  // Persists the choice on the lead immediately so it's captured even if
  // the customer drops off before finishing the booking form.
  const handleScreenCheckAnswer = async (choice) => {
    setFormData({ ...formData, screen_check_preference: choice });
    trackFunnelEvent(10, "complete", { screen_check_preference: choice });

    if (leadId) {
      try {
        await axios.patch(`${API}/leads/${leadId}/screen-check-preference`, {
          screen_check_preference: choice,
        });
      } catch (err) {
        // Non-fatal — the choice is still sent with the booking POST below.
        console.error("Failed to persist screen check preference:", err);
      }
    }

    setStep(8);
    if ((formData.zipCode || formData.preEstimateZip || "").length === 5) {
      setTimeout(() => fetchAvailableSlots(), 200);
    }
  };

  // Number selector component
  const NumberSelector = ({ value, onChange, numbers = [1, 2, 3, 4, 5, 6, 7, 8, 9, 10], max = 20, note = null }) => (
    <div className="space-y-3">
      <div className="flex flex-wrap gap-2">
        {numbers.map((num) => (
          <button
            key={num}
            type="button"
            onClick={() => onChange(num.toString())}
            className={`w-12 h-12 rounded-xl font-bold text-lg transition-all ${
              value === num.toString()
                ? "bg-[#207024] text-white shadow-lg scale-105"
                : "bg-white border-2 border-zinc-200 text-zinc-700 hover:border-[#207024]"
            }`}
          >
            {num}
          </button>
        ))}
      </div>
      <div className="flex items-center gap-2">
        <span className="text-sm text-zinc-500">Or enter:</span>
        <input
          type="number"
          min={numbers[0] || 1}
          max={max}
          value={value}
          onChange={(e) => onChange(e.target.value)}
          className="w-20 h-10 rounded-lg border-2 border-zinc-200 text-center font-bold focus:border-[#207024]"
        />
      </div>
      {note && <p className="text-sm text-zinc-500 italic">{note}</p>}
    </div>
  );

  // V2: Condition radio card
  const ConditionOption = ({ selected, onClick, title, description, testid }) => (
    <button
      type="button"
      onClick={onClick}
      data-testid={testid}
      className={`w-full text-left p-4 rounded-xl border-2 transition-all flex items-start gap-3 ${
        selected
          ? "border-[#207024] bg-green-50"
          : "border-zinc-200 bg-white hover:border-zinc-300"
      }`}
    >
      <span
        className={`mt-1 w-5 h-5 rounded-full border-2 flex-shrink-0 flex items-center justify-center transition-colors ${
          selected ? "border-[#207024] bg-[#207024]" : "border-zinc-300 bg-white"
        }`}
      >
        {selected && <span className="w-2 h-2 rounded-full bg-white" />}
      </span>
      <span className="flex-1">
        <span className="block font-semibold text-zinc-900">{title}</span>
        <span className="block text-sm text-zinc-600 mt-1 leading-snug">{description}</span>
      </span>
    </button>
  );

  // Breakdown input component
  const BreakdownInput = ({ label, value, onChange, description, max = 99 }) => {
    const numValue = parseInt(value) || 0;
    
    const decrement = () => {
      if (numValue > 0) {
        onChange((numValue - 1).toString());
      }
    };
    
    const increment = () => {
      if (numValue < max) {
        onChange((numValue + 1).toString());
      }
    };
    
    return (
      <div className="bg-white rounded-xl border-2 border-zinc-100 p-4">
        <label className="block font-semibold text-zinc-800 mb-1">{label}</label>
        {description && <p className="text-sm text-zinc-500 mb-3">{description}</p>}
        <div className="flex items-center justify-center gap-4">
          <button
            type="button"
            onClick={decrement}
            disabled={numValue <= 0}
            className="w-14 h-14 rounded-full bg-zinc-100 hover:bg-zinc-200 disabled:opacity-40 disabled:cursor-not-allowed flex items-center justify-center transition-colors"
          >
            <span className="text-2xl font-bold text-zinc-700">−</span>
          </button>
          <span className="w-16 text-center text-3xl font-bold text-zinc-900">{numValue}</span>
          <button
            type="button"
            onClick={increment}
            disabled={numValue >= max}
            className="w-14 h-14 rounded-full bg-[#207024] hover:bg-[#185a1c] disabled:opacity-40 disabled:cursor-not-allowed flex items-center justify-center transition-colors"
          >
            <span className="text-2xl font-bold text-white">+</span>
          </button>
        </div>
      </div>
    );
  };

  const estimate = calculateEstimate();

  // Show appointment details if customer already booked
  if (showAppointmentDetails && resumeData?.booked) {
    const { appointment, estimate_data } = resumeData;
    return (
      <div className="min-h-screen bg-gradient-to-b from-zinc-50 to-white">
        {/* Header */}
        <div className="bg-[#207024] text-white py-4 px-4">
          <div className="max-w-lg mx-auto flex items-center justify-between">
            <img src="https://customer-assets.emergentagent.com/job_tech-dispatch-31/artifacts/a6ta9f48_SCREENZZ-LOGO-RGB_TextOnly.png" alt="ScreenzZ" className="h-8" />
            <div className="flex items-center gap-2">
              <CheckCircle className="w-5 h-5" />
              <span className="font-semibold">Appointment Confirmed</span>
            </div>
          </div>
        </div>

        <div className="max-w-lg mx-auto px-4 py-8">
          <div className="text-center mb-8">
            <div className="w-20 h-20 bg-green-100 rounded-full flex items-center justify-center mx-auto mb-4">
              <CheckCircle className="w-10 h-10 text-[#207024]" />
            </div>
            <h1 className="text-2xl font-bold text-zinc-900 mb-2">You're All Set!</h1>
            <p className="text-zinc-600">Your appointment has already been booked.</p>
          </div>

          <div className="bg-white rounded-2xl border-2 border-green-200 p-6 shadow-sm mb-6">
            <h2 className="font-semibold text-lg text-zinc-900 mb-4 flex items-center gap-2">
              <Calendar className="w-5 h-5 text-[#207024]" />
              Appointment Details
            </h2>
            
            <div className="space-y-3">
              {appointment?.date && (
                <div className="flex justify-between">
                  <span className="text-zinc-600">Date:</span>
                  <span className="font-medium">{new Date(appointment.date + 'T12:00:00').toLocaleDateString('en-US', { weekday: 'long', month: 'long', day: 'numeric' })}</span>
                </div>
              )}
              {appointment?.time && (
                <div className="flex justify-between">
                  <span className="text-zinc-600">Time:</span>
                  <span className="font-medium">{appointment.time}</span>
                </div>
              )}
              {appointment?.address && (
                <div className="flex justify-between">
                  <span className="text-zinc-600">Address:</span>
                  <span className="font-medium text-right max-w-[200px]">{appointment.address}</span>
                </div>
              )}
              {appointment?.tech_name && (
                <div className="flex justify-between">
                  <span className="text-zinc-600">Technician:</span>
                  <span className="font-medium">{appointment.tech_name}</span>
                </div>
              )}
            </div>
          </div>

          {estimate_data && <QuoteBreakdown estimate={estimate_data} />}

          <p className="text-center text-zinc-500 text-sm">
            Questions? Call us at <a href="tel:+18555004416" className="text-[#207024] font-medium">(855) 500-4416</a>
          </p>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-gradient-to-b from-zinc-50 to-white">
      {/* Header */}
      <div className="bg-[#207024] text-white py-4 px-4">
        <div className="max-w-lg mx-auto flex items-center justify-between">
          <img src="https://customer-assets.emergentagent.com/job_tech-dispatch-31/artifacts/a6ta9f48_SCREENZZ-LOGO-RGB_TextOnly.png" alt="ScreenzZ" className="h-8" />
          <div className="flex items-center gap-2">
            <Calculator className="w-5 h-5" />
            <span className="font-semibold">Instant Quote</span>
          </div>
        </div>
      </div>

      {/* Progress */}
      {step < 8 && (
        <div className="max-w-lg mx-auto px-4 pt-4">
          <div className="h-2 bg-zinc-200 rounded-full overflow-hidden">
            <div
              className="h-full bg-[#207024] transition-all duration-500"
              style={{ width: `${Math.min((step + 1) / 8 * 100, 100)}%` }}
            />
          </div>
        </div>
      )}

      {/* Promotion Banner (shows on all steps except step 7 which has its own larger banner) */}
      {promotion && step !== 7 && (
        <div className="max-w-lg mx-auto px-4 pt-4">
          <div className="bg-gradient-to-r from-amber-500 to-orange-500 rounded-xl p-3 text-white">
            <div className="flex items-center justify-between gap-3">
              <div className="flex items-center gap-2">
                <Tag className="w-4 h-4 flex-shrink-0" />
                <div>
                  <p className="font-bold text-sm">{promotion.name}</p>
                  <p className="text-xs opacity-90">
                    Save {promotion.discount_type === "percent" ? `${promotion.discount_value}%` : `$${promotion.discount_value}`}
                  </p>
                </div>
              </div>
              {promoCountdown && (
                <div className="text-right flex-shrink-0">
                  <p className="text-[10px] opacity-75 uppercase tracking-wide">Ends in</p>
                  <p className="text-sm font-bold font-mono">
                    {promoCountdown.days > 0 && `${promoCountdown.days}d `}
                    {String(promoCountdown.hours).padStart(2, '0')}:{String(promoCountdown.minutes).padStart(2, '0')}:{String(promoCountdown.seconds).padStart(2, '0')}
                  </p>
                </div>
              )}
            </div>
          </div>
        </div>
      )}

      <div className="max-w-lg mx-auto px-4 py-6">
        {/* Step 0: Service Type */}
        {step === 0 && (headlineExp.ready ? (
          <ServiceTypeStep
            value={formData.serviceType}
            onSelect={(v) => setFormData({ ...formData, serviceType: v })}
            variant={headlineExp.variant}
          />
        ) : (
          <div className="min-h-[380px]" data-testid="step-service-type-loading" aria-hidden="true" />
        ))}

        {/* Step 1: Window Quantity */}
        {step === 1 && isMemorialDayActive && (
          <div className="space-y-5" data-testid="step-window-quantity-memorial">
            <div>
              <h1 className="text-2xl font-bold text-zinc-900">How many window screens do you need?</h1>
              <p className="text-zinc-500 mt-1">Window screens only (not sliding doors)</p>
            </div>

            {/* Memorial Day tier-aligned defaults */}
            <div className="space-y-3">
              {(() => {
                // Compute the "next tier to push toward" based on the custom
                // value. When the customer types 13 we want to glow the 15
                // card with an "Upgrade" prompt — gentle psychological nudge.
                const customRaw = parseInt(formData.totalWindowScreens) || 0;
                const isCustomActive = customRaw > 0 && ![10, 15, 20].includes(customRaw);
                const nextTier = isCustomActive ? memorialNextTierAway(customRaw) : null;
                const glowingQty = nextTier ? nextTier.nextQty : null;
                return [
                { qty: 10, savings: "Save $100", sub: "Perfect for smaller whole-home refreshes" },
                { qty: 15, savings: "Save $200", sub: "Most popular whole-home option", badge: "Most Popular" },
                { qty: 20, savings: "FREE Sliding Screen Door", sub: "$426 value — best deal for larger homes", badge: "Best Value", highlight: true },
              ].map((tier) => {
                const isSelected = parseInt(formData.totalWindowScreens) === tier.qty;
                const isGlowing = !isSelected && glowingQty === tier.qty;
                return (
                  <button
                    key={tier.qty}
                    onClick={() => setFormData({
                      ...formData,
                      totalWindowScreens: String(tier.qty),
                      // In Memorial Day mode the customer may mix rescreens and
                      // new builds freely — we default to all new builds here
                      // and let the next step adjust the split.
                      windowNewBuilds: String(tier.qty),
                      windowRescreens: "0",
                      windowNotSure: "",
                      windowCondition: "",
                    })}
                    data-testid={`memorial-tier-pick-${tier.qty}`}
                    className={`relative w-full text-left p-5 rounded-2xl border-2 transition-all ${
                      isSelected
                        ? "border-[#207024] bg-green-50 shadow-md"
                        : isGlowing
                          ? "border-[#207024] bg-gradient-to-br from-amber-50 to-green-50 shadow-lg ring-4 ring-[#207024]/15 animate-pulse"
                          : tier.highlight
                            ? "border-[#207024]/40 bg-gradient-to-br from-green-50/40 to-white hover:border-[#207024]"
                            : "border-zinc-200 bg-white hover:border-zinc-400"
                    }`}
                  >
                    {tier.badge && (
                      <div className="absolute -top-3 right-4 bg-amber-400 text-zinc-900 text-[10px] font-bold px-2.5 py-1 rounded-full uppercase tracking-wide whitespace-nowrap">
                        {tier.badge}
                      </div>
                    )}
                    <div className="flex items-baseline justify-between gap-3">
                      <div>
                        <div className="text-3xl font-extrabold text-zinc-900 leading-none">
                          {tier.qty}
                          <span className="ml-1 text-sm font-semibold text-zinc-500">screens</span>
                        </div>
                        <p className="mt-1.5 text-sm text-zinc-600">{tier.sub}</p>
                      </div>
                      <div className={`shrink-0 text-right text-[#207024]`}>
                        <div className="text-base sm:text-lg font-bold leading-tight">
                          {tier.savings}
                        </div>
                      </div>
                    </div>
                    {isGlowing && (
                      <div
                        data-testid={`memorial-glow-nudge-${tier.qty}`}
                        className="mt-3 text-xs font-bold text-[#b22234] uppercase tracking-wide"
                      >
                        ↑ Tap to upgrade — just {nextTier.needed} more for {nextTier.unlocks}
                      </div>
                    )}
                  </button>
                );
              });
              })()}
            </div>

            {/* Custom amount */}
            <div className="rounded-2xl border-2 border-dashed border-zinc-300 p-4">
              <label className="block text-sm font-semibold text-zinc-700 mb-2">
                Or enter custom amount
              </label>
              <div className="flex items-center gap-3">
                <input
                  type="number"
                  inputMode="numeric"
                  min="1"
                  max="100"
                  value={
                    [10, 15, 20].includes(parseInt(formData.totalWindowScreens))
                      ? ""
                      : formData.totalWindowScreens || ""
                  }
                  onChange={(e) => {
                    const val = e.target.value.replace(/[^\d]/g, "");
                    setFormData({
                      ...formData,
                      totalWindowScreens: val,
                      windowNewBuilds: val,
                      windowRescreens: "0",
                      windowNotSure: "",
                      windowCondition: "",
                    });
                  }}
                  placeholder="e.g. 13"
                  data-testid="memorial-custom-amount-input"
                  className="w-32 h-12 rounded-xl border-2 border-zinc-200 px-3 text-lg font-semibold focus:border-[#207024] focus:outline-none"
                />
                <span className="text-sm text-zinc-500">screens</span>
              </div>
              {/* Live savings hint for custom amounts */}
              {(() => {
                const v = parseInt(formData.totalWindowScreens) || 0;
                if (!v || [10, 15, 20].includes(v)) return null;
                const tier = getMemorialDayTier(v);
                const away = memorialNextTierAway(v);
                if (tier.tier > 0) {
                  return (
                    <p className="mt-3 text-sm font-semibold text-[#207024]">
                      🎉 You unlocked {tier.label}
                      {away && (
                        <span className="block text-zinc-600 font-normal mt-0.5">
                          Add {away.needed} more to unlock {away.unlocks}.
                        </span>
                      )}
                    </p>
                  );
                }
                if (away) {
                  return (
                    <p className="mt-3 text-sm text-zinc-600">
                      You're {away.needed} screen{away.needed === 1 ? "" : "s"} away from {away.unlocks}.
                    </p>
                  );
                }
                return null;
              })()}
            </div>

            {/* Mobile-service-minimum heads-up */}
            {formData.totalWindowScreens && parseInt(formData.totalWindowScreens) <= 2 && (
              <div className="bg-amber-50 border border-amber-200 rounded-xl p-4 text-sm">
                <p className="text-amber-800">
                  <strong>Heads up!</strong> Our mobile service requires a minimum of 3 screens. Under 3 you can still get a quote — just drop off your screens at our shop.
                </p>
              </div>
            )}
          </div>
        )}

        {step === 1 && !isMemorialDayActive && (
          <div className="space-y-6" data-testid="step-window-quantity">
            <div>
              <h1 className="text-2xl font-bold text-zinc-900">How many window screens do you need?</h1>
              <p className="text-zinc-500 mt-1">Window screens only (not sliding doors)</p>
            </div>
            
            <NumberSelector
              value={formData.totalWindowScreens}
              onChange={(val) => {
                setFormData({ ...formData, totalWindowScreens: val, windowRescreens: "", windowNewBuilds: "", windowNotSure: "", windowCondition: "" });
              }}
              numbers={[1, 2, 3, 4, 5, 6, 7, 8, 9, 10, 12, 15, 20, 25]}
              max={25}
            />

            {/* Note for 1-2 screens about mobile service minimum */}
            {formData.totalWindowScreens && parseInt(formData.totalWindowScreens) <= 2 && (
              <div className="bg-amber-50 border border-amber-200 rounded-xl p-4 text-sm">
                <p className="text-amber-800">
                  <strong>Heads up!</strong> Our mobile service requires a minimum of 3 screens (windows + doors combined). 
                  If your total is under 3, you can still get a quote — just drop off your screens at our shop and pick them up when ready.
                </p>
              </div>
            )}
          </div>
        )}

        {/* Step 2: Window Breakdown */}
        {step === 2 && !funnelV2Enabled && (
          <div className="space-y-6" data-testid="step-window-breakdown">
            {/* Memorial Day Sale dynamic qualification banner */}
            {isMemorialDayActive && (() => {
              // Tier is based on TOTAL window screens (rescreens + new builds).
              const totalWindows = (parseInt(formData.windowRescreens) || 0) + (parseInt(formData.windowNewBuilds) || 0) + (parseInt(formData.windowNotSure) || 0);
              const tier = getMemorialDayTier(totalWindows);
              const away = memorialNextTierAway(totalWindows);
              const isWinning = tier.tier > 0;
              return (
                <div
                  data-testid="memorial-tier-banner"
                  className={`rounded-2xl p-4 border-2 ${
                    isWinning ? "bg-green-50 border-green-300" : "bg-amber-50 border-amber-200"
                  }`}
                >
                  <div className="text-xs font-bold uppercase tracking-wide text-[#b22234]">
                    🇺🇸 Memorial Day Sale
                  </div>
                  {isWinning ? (
                    <p className="mt-1 text-sm font-semibold text-green-800">
                      🎉 You unlocked {tier.label}!
                      {away && (
                        <span className="block mt-1 font-normal text-green-700">
                          Add {away.needed} more screen{away.needed === 1 ? "" : "s"} to unlock {away.unlocks}.
                        </span>
                      )}
                    </p>
                  ) : (
                    <p className="mt-1 text-sm font-semibold text-zinc-800">
                      You're {away.needed} screen{away.needed === 1 ? "" : "s"} away from unlocking {away.unlocks}.
                    </p>
                  )}
                </div>
              );
            })()}
            <div>
              <h1 className="text-2xl font-bold text-zinc-900">
                Out of your {formData.totalWindowScreens} window screens, how many need:
              </h1>
            </div>
            
            <div className="space-y-4">
              <BreakdownInput
                label="Rescreening"
                description="Frames are in good shape — not bent or broken. We replace just the mesh."
                value={formData.windowRescreens}
                max={parseInt(formData.totalWindowScreens) || 0}
                onChange={(val) => setFormData({ ...formData, ...splitCounts("window", "totalWindowScreens", "r", val) })}
              />
              
              <BreakdownInput
                label="New Builds"
                description="Built from scratch — new frame, hardware, and mesh included."
                value={formData.windowNewBuilds}
                max={parseInt(formData.totalWindowScreens) || 0}
                onChange={(val) => setFormData({ ...formData, ...splitCounts("window", "totalWindowScreens", "n", val) })}
              />

              <BreakdownInput
                label="Not sure"
                description="Our tech will check your frames on-site. You won't pay for a new frame unless you approve it."
                value={formData.windowNotSure}
                max={parseInt(formData.totalWindowScreens) || 0}
                onChange={(val) => setFormData({ ...formData, ...splitCounts("window", "totalWindowScreens", "u", val) })}
              />
            </div>

            {/* Validation */}
            {!windowBreakdownValid() && (formData.windowRescreens || formData.windowNewBuilds || formData.windowNotSure) && (
              <div className="flex items-center gap-2 text-amber-600 bg-amber-50 p-3 rounded-lg">
                <AlertCircle className="w-5 h-5" />
                <span className="text-sm">
                  Total must equal {formData.totalWindowScreens}. 
                  Current: {(parseInt(formData.windowRescreens) || 0) + (parseInt(formData.windowNewBuilds) || 0) + (parseInt(formData.windowNotSure) || 0)}
                </span>
              </div>
            )}
          </div>
        )}

        {/* Step 2 (V2): Window Condition Picker */}
        {step === 2 && funnelV2Enabled && (
          <div className="space-y-5" data-testid="step-window-condition">
            <div>
              <h1 className="text-2xl font-bold text-zinc-900">What condition are your window screens in?</h1>
            </div>

            {/* Quantity inputs (lead with these) */}
            <div className="space-y-3">
              <BreakdownInput
                label="Rescreening"
                description="What is rescreening? If your frame is still in good shape but the mesh is torn or sagging, we replace just the mesh — no need for a whole new screen."
                value={formData.windowRescreens || "0"}
                max={parseInt(formData.totalWindowScreens) || 0}
                onChange={(val) => {
                  const total = parseInt(formData.totalWindowScreens) || 0;
                  const rescreens = parseInt(val) || 0;
                  setFormData({
                    ...formData,
                    windowRescreens: val,
                    windowNewBuilds: Math.max(0, total - rescreens).toString(),
                    windowCondition: ""
                  });
                }}
              />

              <BreakdownInput
                label="New Screens"
                description="A brand new screen — we measure on-site, then build to fit with a new frame, mesh, and hardware, all installed for you."
                value={formData.windowNewBuilds || "0"}
                max={parseInt(formData.totalWindowScreens) || 0}
                onChange={(val) => {
                  const total = parseInt(formData.totalWindowScreens) || 0;
                  const newBuilds = parseInt(val) || 0;
                  setFormData({
                    ...formData,
                    windowNewBuilds: val,
                    windowRescreens: Math.max(0, total - newBuilds).toString(),
                    windowCondition: ""
                  });
                }}
              />

              {/* Validation */}
              {!windowBreakdownValid() && ((parseInt(formData.windowRescreens) || 0) + (parseInt(formData.windowNewBuilds) || 0) + (parseInt(formData.windowNotSure) || 0) > 0) && (
                <div className="flex items-center gap-2 text-amber-600 bg-amber-50 p-3 rounded-lg" data-testid="window-breakdown-error">
                  <AlertCircle className="w-5 h-5 flex-shrink-0" />
                  <span className="text-sm">
                    Total must match your selected number of screens (
                    {formData.totalWindowScreens}). Current:{" "}
                    {(parseInt(formData.windowRescreens) || 0) + (parseInt(formData.windowNewBuilds) || 0) + (parseInt(formData.windowNotSure) || 0)}
                  </span>
                </div>
              )}
            </div>
          </div>
        )}

        {/* Step 3: Slider Quantity */}
        {step === 3 && (
          <div className="space-y-6" data-testid="step-slider-quantity">
            <div>
              <h1 className="text-2xl font-bold text-zinc-900">How many screen doors do you need?</h1>
            </div>
            
            <NumberSelector
              value={formData.totalSliders}
              onChange={(val) => setFormData({ ...formData, totalSliders: val, sliderRescreens: "", sliderNewBuilds: "", sliderNotSure: "", sliderCondition: "" })}
              numbers={[1, 2, 3, 4]}
              max={4}
            />
          </div>
        )}

        {/* Step 4: Slider Breakdown + PetScreen */}
        {step === 4 && (
          <div className="space-y-6" data-testid="step-slider-breakdown">
            {/* V1: Quantity split inputs */}
            {!funnelV2Enabled && (
              <>
                <div>
                  <h1 className="text-2xl font-bold text-zinc-900">
                    Out of your {formData.totalSliders} sliding screen door{parseInt(formData.totalSliders) === 1 ? "" : "s"}, how many need:
                  </h1>
                </div>

                <div className="space-y-4">
                  <BreakdownInput
                    label="Slider Rescreen"
                    description="Frame is in good shape — not bent or broken. We replace just the mesh."
                    value={formData.sliderRescreens}
                    max={parseInt(formData.totalSliders) || 0}
                    onChange={(val) => {
                      const next = splitCounts("slider", "totalSliders", "r", val);
                      setFormData({ ...formData, ...next, wantsDisposal: parseInt(next.sliderNewBuilds) > 0 ? formData.wantsDisposal : false });
                    }}
                  />

                  <BreakdownInput
                    label="New Sliding Screen Door"
                    description="Built from scratch — new frame, rollers, hardware, and mesh included."
                    value={formData.sliderNewBuilds}
                    max={parseInt(formData.totalSliders) || 0}
                    onChange={(val) => {
                      const next = splitCounts("slider", "totalSliders", "n", val);
                      setFormData({ ...formData, ...next, wantsDisposal: parseInt(next.sliderNewBuilds) > 0 ? formData.wantsDisposal : false });
                    }}
                  />

                  <BreakdownInput
                    label="Not sure"
                    description="Our tech will check your frames on-site. You won't pay for a new frame unless you approve it."
                    value={formData.sliderNotSure}
                    max={parseInt(formData.totalSliders) || 0}
                    onChange={(val) => {
                      const next = splitCounts("slider", "totalSliders", "u", val);
                      setFormData({ ...formData, ...next, wantsDisposal: parseInt(next.sliderNewBuilds) > 0 ? formData.wantsDisposal : false });
                    }}
                  />
                </div>

                {/* Validation */}
                {!sliderBreakdownValid() && (formData.sliderRescreens || formData.sliderNewBuilds || formData.sliderNotSure) && (
                  <div className="flex items-center gap-2 text-amber-600 bg-amber-50 p-3 rounded-lg">
                    <AlertCircle className="w-5 h-5" />
                    <span className="text-sm">
                      Total must equal {formData.totalSliders}.
                      Current: {(parseInt(formData.sliderRescreens) || 0) + (parseInt(formData.sliderNewBuilds) || 0) + (parseInt(formData.sliderNotSure) || 0)}
                    </span>
                  </div>
                )}
              </>
            )}

            {/* V2: Quantity-led picker with Not Sure below */}
            {funnelV2Enabled && (
              <div className="space-y-5" data-testid="step-slider-condition">
                <div>
                  <h1 className="text-2xl font-bold text-zinc-900">What condition are your screen doors in?</h1>
                  <p className="text-zinc-500 mt-2 text-sm">
                    Don't worry if you're not 100% sure — we'll confirm everything before finalizing your order.
                  </p>
                </div>

                {/* Quantity inputs (lead with these) */}
                <div className="space-y-3">
                  <BreakdownInput
                    label="Slider Rescreening"
                    description="Frame is in good shape — we replace the mesh only."
                    value={formData.sliderRescreens || "0"}
                    max={parseInt(formData.totalSliders) || 0}
                    onChange={(val) => {
                      const total = parseInt(formData.totalSliders) || 0;
                      const rescreens = parseInt(val) || 0;
                      const newBuilds = Math.max(0, total - rescreens);
                      setFormData({
                        ...formData,
                        sliderRescreens: val,
                        sliderNewBuilds: newBuilds.toString(),
                        sliderCondition: "", // manual edit clears Not Sure
                        wantsDisposal: newBuilds > 0 ? formData.wantsDisposal : false
                      });
                    }}
                  />

                  <BreakdownInput
                    label="New Sliding Screen Door"
                    description="Brand new door — frame, rollers, hardware, and mesh."
                    value={formData.sliderNewBuilds || "0"}
                    max={parseInt(formData.totalSliders) || 0}
                    onChange={(val) => {
                      const total = parseInt(formData.totalSliders) || 0;
                      const newBuilds = parseInt(val) || 0;
                      setFormData({
                        ...formData,
                        sliderNewBuilds: val,
                        sliderRescreens: Math.max(0, total - newBuilds).toString(),
                        sliderCondition: "", // manual edit clears Not Sure
                        wantsDisposal: newBuilds > 0 ? formData.wantsDisposal : false
                      });
                    }}
                  />

                  {/* Validation */}
                  {!sliderBreakdownValid() && ((parseInt(formData.sliderRescreens) || 0) + (parseInt(formData.sliderNewBuilds) || 0) + (parseInt(formData.sliderNotSure) || 0) > 0) && (
                    <div className="flex items-center gap-2 text-amber-600 bg-amber-50 p-3 rounded-lg" data-testid="slider-breakdown-error">
                      <AlertCircle className="w-5 h-5 flex-shrink-0" />
                      <span className="text-sm">
                        Total must match your selected number of doors (
                        {formData.totalSliders}). Current:{" "}
                        {(parseInt(formData.sliderRescreens) || 0) + (parseInt(formData.sliderNewBuilds) || 0) + (parseInt(formData.sliderNotSure) || 0)}
                      </span>
                    </div>
                  )}
                </div>

                {/* Not Sure option below count inputs */}
                <ConditionOption
                  testid="slider-condition-not-sure"
                  selected={formData.sliderCondition === "not_sure"}
                  onClick={() => applySliderCondition("not_sure")}
                  title="Not Sure"
                  description="We'll quote it and confirm on-site."
                />

                <p className="text-xs text-zinc-500 italic text-center pt-1">
                  We'll confirm everything during service and adjust if needed.
                </p>
              </div>
            )}

                {/* Stacked Upsells: PetScreen + Solar + Disposal */}
            {sliderBreakdownValid() && parseInt(formData.totalSliders) > 0 && (
              <div className="space-y-3">
                <h2 className="text-base font-semibold text-zinc-900 pt-2" data-testid="mesh-upgrades-header">
                  Screen Mesh Upgrades <span className="font-normal text-zinc-500">(Optional)</span>
                </h2>
                {/* PetScreen Upgrade - First Upsell */}
                <div className="bg-green-50 rounded-xl p-4 border-2 border-green-100 relative">
                  {/* Most Popular tag */}
                  <span className="absolute -top-2 right-3 bg-amber-500 text-white text-[10px] font-bold px-2 py-0.5 rounded-full uppercase tracking-wide">
                    Most Popular
                  </span>

                  <h3 className="font-semibold text-zinc-900" data-testid="petscreen-upsell-title">
                    PetScreen (+${Number(pricing.petScreenUpgrade || 0).toFixed(0)} per door)
                  </h3>
                  <p className="text-sm text-zinc-600">7x Stronger for Pets &amp; Children</p>
                  <MeshPhoto kind="petscreen" />

                  <div className="flex gap-3">
                    <button
                      data-testid="petscreen-no-thanks-btn"
                      onClick={() => setFormData({ ...formData, wantsPetScreen: false, petScreenCount: "" })}
                      className={`flex-1 py-3 rounded-lg font-semibold transition-all ${
                        !formData.wantsPetScreen
                          ? "bg-zinc-800 text-white"
                          : "bg-white border-2 border-zinc-200 text-zinc-700"
                      }`}
                    >
                      No Thanks
                    </button>
                    <button
                      data-testid="petscreen-yes-upgrade-btn"
                      onClick={() => setFormData({
                        ...formData,
                        wantsPetScreen: true,
                        petScreenCount: formData.totalSliders,
                        // Mutually exclusive with Solar — clear Solar selection.
                        wantsSolarScreen: false,
                        solarScreenCount: "",
                      })}
                      className={`flex-1 py-3 rounded-lg font-semibold transition-all ${
                        formData.wantsPetScreen
                          ? "bg-[#207024] text-white"
                          : "bg-white border-2 border-zinc-200 text-zinc-700"
                      }`}
                    >
                      Yes, Upgrade
                    </button>
                  </div>

                  {formData.wantsPetScreen && (
                    <div className="mt-4">
                      <label className="text-sm font-medium text-zinc-700">How many doors to upgrade?</label>
                      <div className="flex items-center justify-center gap-4 mt-2">
                        <button
                          type="button"
                          onClick={() => {
                            const current = parseInt(formData.petScreenCount) || 0;
                            if (current > 1) setFormData({ ...formData, petScreenCount: (current - 1).toString() });
                          }}
                          disabled={(parseInt(formData.petScreenCount) || 0) <= 1}
                          className="w-12 h-12 rounded-full bg-zinc-100 hover:bg-zinc-200 disabled:opacity-40 disabled:cursor-not-allowed flex items-center justify-center transition-colors"
                        >
                          <span className="text-xl font-bold text-zinc-700">−</span>
                        </button>
                        <span className="w-12 text-center text-2xl font-bold text-zinc-900">{parseInt(formData.petScreenCount) || 0}</span>
                        <button
                          type="button"
                          onClick={() => {
                            const current = parseInt(formData.petScreenCount) || 0;
                            const max = parseInt(formData.totalSliders) || 0;
                            if (current < max) setFormData({ ...formData, petScreenCount: (current + 1).toString() });
                          }}
                          disabled={(parseInt(formData.petScreenCount) || 0) >= (parseInt(formData.totalSliders) || 0)}
                          className="w-12 h-12 rounded-full bg-[#207024] hover:bg-[#185a1c] disabled:opacity-40 disabled:cursor-not-allowed flex items-center justify-center transition-colors"
                        >
                          <span className="text-xl font-bold text-white">+</span>
                        </button>
                      </div>
                    </div>
                  )}
                </div>

                {/* Solar Screen Upgrade — Second Upsell (mutually exclusive
                    with PetScreen). No "Most Popular" badge by design. */}
                <div className="bg-amber-50 rounded-xl p-4 border-2 border-amber-100">
                  <h3 className="font-semibold text-zinc-900" data-testid="solarscreen-upsell-title">
                    Solar Screen (+${Number(pricing.solarDoor || 0).toFixed(0)} per door)
                  </h3>
                  <p className="text-sm text-zinc-600">5x Stronger • Blocks Heat &amp; UV • Lowers AC Bill</p>
                  <MeshPhoto kind="solar" />

                  <div className="flex gap-3">
                    <button
                      data-testid="solarscreen-no-thanks-btn"
                      onClick={() => setFormData({ ...formData, wantsSolarScreen: false, solarScreenCount: "" })}
                      className={`flex-1 py-3 rounded-lg font-semibold transition-all ${
                        !formData.wantsSolarScreen
                          ? "bg-zinc-800 text-white"
                          : "bg-white border-2 border-zinc-200 text-zinc-700"
                      }`}
                    >
                      No Thanks
                    </button>
                    <button
                      data-testid="solarscreen-yes-upgrade-btn"
                      onClick={() => setFormData({
                        ...formData,
                        wantsSolarScreen: true,
                        solarScreenCount: formData.totalSliders,
                        // Mutually exclusive with PetScreen — clear PetScreen selection.
                        wantsPetScreen: false,
                        petScreenCount: "",
                      })}
                      className={`flex-1 py-3 rounded-lg font-semibold transition-all ${
                        formData.wantsSolarScreen
                          ? "bg-[#207024] text-white"
                          : "bg-white border-2 border-zinc-200 text-zinc-700"
                      }`}
                    >
                      Yes, Upgrade
                    </button>
                  </div>

                  {formData.wantsSolarScreen && (
                    <div className="mt-4">
                      <label className="text-sm font-medium text-zinc-700">How many doors to upgrade?</label>
                      <div className="flex items-center justify-center gap-4 mt-2">
                        <button
                          type="button"
                          onClick={() => {
                            const current = parseInt(formData.solarScreenCount) || 0;
                            if (current > 1) setFormData({ ...formData, solarScreenCount: (current - 1).toString() });
                          }}
                          disabled={(parseInt(formData.solarScreenCount) || 0) <= 1}
                          className="w-12 h-12 rounded-full bg-zinc-100 hover:bg-zinc-200 disabled:opacity-40 disabled:cursor-not-allowed flex items-center justify-center transition-colors"
                        >
                          <span className="text-xl font-bold text-zinc-700">−</span>
                        </button>
                        <span className="w-12 text-center text-2xl font-bold text-zinc-900">{parseInt(formData.solarScreenCount) || 0}</span>
                        <button
                          type="button"
                          onClick={() => {
                            const current = parseInt(formData.solarScreenCount) || 0;
                            const max = parseInt(formData.totalSliders) || 0;
                            if (current < max) setFormData({ ...formData, solarScreenCount: (current + 1).toString() });
                          }}
                          disabled={(parseInt(formData.solarScreenCount) || 0) >= (parseInt(formData.totalSliders) || 0)}
                          className="w-12 h-12 rounded-full bg-[#207024] hover:bg-[#185a1c] disabled:opacity-40 disabled:cursor-not-allowed flex items-center justify-center transition-colors"
                        >
                          <span className="text-xl font-bold text-white">+</span>
                        </button>
                      </div>
                    </div>
                  )}
                </div>

                {/* Old Door Disposal Upsell - Only for New Sliding Screen Doors */}
                {parseInt(formData.sliderNewBuilds) > 0 && (
                  <div className="bg-zinc-50 rounded-xl p-4 border-2 border-zinc-200">
                    <h3 className="font-semibold text-zinc-900 mb-1">
                      Want us to take your old screen door?
                    </h3>
                    <p className="text-sm text-zinc-600 mb-2">+$10 per door • We'll haul it away and recycle it</p>

                    {/* Helper text */}
                    <p className="text-xs text-zinc-400 mb-4">
                      {funnelV2Enabled
                        ? "We'll haul away and recycle your old screens."
                        : "Skip the hassle — we'll remove and dispose of your old door for you."}
                    </p>

                    <div className="flex gap-3">
                      <button
                        onClick={() => setFormData({ ...formData, wantsDisposal: false })}
                        className={`flex-1 py-3 rounded-lg font-semibold transition-all ${
                          !formData.wantsDisposal
                            ? "bg-zinc-800 text-white"
                            : "bg-white border-2 border-zinc-200 text-zinc-700"
                        }`}
                      >
                        No Thanks
                      </button>
                      <button
                        onClick={() => setFormData({ ...formData, wantsDisposal: true })}
                        className={`flex-1 py-3 rounded-lg font-semibold transition-all ${
                          formData.wantsDisposal
                            ? "bg-[#207024] text-white"
                            : "bg-white border-2 border-zinc-200 text-zinc-700"
                        }`}
                      >
                        Yes, Take It Away
                      </button>
                    </div>
                  </div>
                )}
              </div>
            )}
          </div>
        )}

        {/* Step 5: Contact Information */}
        {step === 5 && (
          <div className="space-y-6" data-testid="step-contact">
            <div>
              <h1 className="text-2xl font-bold text-zinc-900">Where should we send your instant quote?</h1>
            </div>

            {/* What's Next? (now where the review card used to be) */}
            <div className="rounded-xl bg-green-50 border border-green-200 p-4" data-testid="trust-whats-next">
              <p className="text-xs font-semibold text-[#207024] uppercase tracking-wide mb-2">
                What's Next?
              </p>
              <ul className="space-y-1.5 text-sm text-zinc-700">
                <li className="flex items-start gap-2">
                  <CheckCircle className="w-4 h-4 text-[#207024] mt-0.5 shrink-0" />
                  <span>See your price <strong>instantly</strong> on the next page</span>
                </li>
                <li className="flex items-start gap-2">
                  <CheckCircle className="w-4 h-4 text-[#207024] mt-0.5 shrink-0" />
                  <span>Book whenever you're ready</span>
                </li>
              </ul>
            </div>

            <div className="space-y-4">
              <div>
                <label className="block font-semibold text-zinc-700 mb-2">First Name</label>
                <input
                  type="text"
                  value={formData.firstName}
                  onChange={(e) => setFormData({ ...formData, firstName: e.target.value })}
                  placeholder="John"
                  className="w-full h-14 rounded-xl border-2 border-zinc-200 px-4 text-lg focus:border-[#207024]"
                />
              </div>
              
              <div>
                <label className="block font-semibold text-zinc-700 mb-2">Phone Number</label>
                <input
                  type="tel"
                  value={formData.phone}
                  onChange={(e) => setFormData({ ...formData, phone: formatPhoneNumber(e.target.value) })}
                  placeholder="(555) 123-4567"
                  maxLength={14}
                  className={`w-full h-14 rounded-xl border-2 px-4 text-lg focus:border-[#207024] ${
                    formData.phone && !isValidPhone(formData.phone) 
                      ? "border-amber-300 bg-amber-50" 
                      : "border-zinc-200"
                  }`}
                />
                {formData.phone && !isValidPhone(formData.phone) && (
                  <p className="text-sm text-amber-600 mt-1">Please enter a 10-digit phone number</p>
                )}

                <p className="text-[11px] text-zinc-400 mt-2 leading-snug">
                  By submitting this form, you agree that ScreenzZ may contact you by call or text regarding your quote, appointment, or service. Message frequency varies. Message and data rates may apply. Reply STOP to opt out.
                </p>
              </div>
              
              {/* Email field - only shown if collectEmailEnabled */}
              {collectEmailEnabled && (
                <div>
                  <label className="block font-semibold text-zinc-700 mb-2">
                    Email
                  </label>
                  <input
                    type="email"
                    value={formData.email}
                    onChange={(e) => setFormData({ ...formData, email: e.target.value })}
                    placeholder="Enter your email"
                    className={`w-full h-14 rounded-xl border-2 px-4 text-lg focus:border-[#207024] ${
                      formData.email && !isValidEmail(formData.email) 
                        ? "border-amber-300 bg-amber-50" 
                        : "border-zinc-200"
                    }`}
                  />
                  <p className="text-xs text-zinc-400 mt-1">Used for receipts and confirmations</p>
                  {formData.email && !isValidEmail(formData.email) && (
                    <p className="text-sm text-amber-600 mt-1">Please enter a valid email address</p>
                  )}
                </div>
              )}
            </div>

            {/* Rotating Google review card + price guarantee — directly above Get My Quote. */}
            <div className="space-y-3">
              {(() => {
                const rev = REVIEW_TESTIMONIALS[reviewIndex] || REVIEW_TESTIMONIALS[0];
                return (
                  <div
                    className="rounded-xl border border-zinc-200 bg-white px-4 py-3"
                    data-testid="trust-reviews-card"
                    data-review-author={rev.author}
                  >
                    <GoogleRatingLine testid="trust-reviews-rating" />
                    {/* All reviews share one grid cell so the card keeps the tallest review's
                        height — the guarantee and button below don't jump on rotation. */}
                    <div className="grid mt-2">
                      {REVIEW_TESTIMONIALS.map((r, i) => (
                        <div key={r.author} className={`[grid-area:1/1] transition-opacity duration-500 ${i === reviewIndex ? "opacity-100" : "opacity-0 invisible"}`}
                          aria-hidden={i !== reviewIndex}>
                          <p className="text-sm text-zinc-600 italic leading-snug">&ldquo;{r.body}&rdquo;</p>
                          <p className="text-xs text-zinc-400 mt-1">
                            — {r.author}
                            {r.badge && <span className="ml-1 text-zinc-400/80">· {r.badge}</span>}
                            <span className="ml-1">· Google review</span>
                          </p>
                        </div>
                      ))}
                    </div>
                    {REVIEW_TESTIMONIALS.length > 1 && (
                      <div className="flex justify-center gap-1.5 mt-2">
                        {REVIEW_TESTIMONIALS.map((_, i) => (
                          <button
                            key={i}
                            type="button"
                            onClick={() => setReviewIndex(i)}
                            data-testid={`review-dot-${i}`}
                            aria-label={`Show review ${i + 1}`}
                            className={`w-1.5 h-1.5 rounded-full transition-colors ${
                              i === reviewIndex ? "bg-[#207024]" : "bg-zinc-300"
                            }`}
                          />
                        ))}
                      </div>
                    )}
                  </div>
                );
              })()}
              <PriceGuarantee />
            </div>
          </div>
        )}

        {/* Step 6: Address (Optional - Before Estimate) */}
        {step === 6 && (
          <div className="space-y-6" data-testid="step-address">
            <div className="text-center">
              <div className="w-16 h-16 bg-teal-100 rounded-full flex items-center justify-center mx-auto mb-4">
                <MapPin className="w-8 h-8 text-teal-600" />
              </div>
              <h1 className="text-2xl font-bold text-zinc-900">Where is the job located?</h1>
              <p className="text-zinc-500 mt-1">Used to confirm we service your area and avoid delays</p>
            </div>

            {/* Street Address with Google Autocomplete */}
            <div>
              <label className="block font-semibold text-zinc-700 mb-2">
                Street Address
              </label>
              <input
                ref={preEstimateAddressRef}
                type="text"
                placeholder="Start typing your address..."
                value={formData.preEstimateAddress}
                onChange={(e) => setFormData({ ...formData, preEstimateAddress: e.target.value })}
                className="w-full h-14 px-4 rounded-xl border-2 border-zinc-200 focus:border-[#207024] text-lg"
              />
            </div>

            {/* ZIP Code (manual fallback) */}
            <div>
              <label className="block font-semibold text-zinc-700 mb-2">
                ZIP Code
              </label>
              <input
                type="text"
                placeholder="Enter ZIP code"
                value={formData.preEstimateZip}
                onChange={(e) => setFormData({ ...formData, preEstimateZip: e.target.value })}
                className="w-full h-14 px-4 rounded-xl border-2 border-zinc-200 focus:border-[#207024] text-lg text-center tracking-widest"
                maxLength={5}
              />
              <p className="text-xs text-zinc-400 mt-1 text-center">
                You can also just enter your ZIP code if you prefer
              </p>
            </div>

            {/* Warning if skipped */}
            {skippedAddress && (
              <div className="bg-amber-50 border border-amber-200 rounded-lg p-3 text-sm text-amber-700">
                We may need to confirm your location before scheduling
              </div>
            )}

            {/* Buttons */}
            <div className="space-y-3 pt-4">
              <button
                onClick={handleNext}
                className="w-full py-4 bg-[#F26C2F] text-white rounded-xl font-bold text-lg hover:bg-[#D9591F] transition-colors"
              >
                Continue to Quote →
              </button>
              
              <button
                onClick={handleSkipAddress}
                className="w-full py-3 text-zinc-500 hover:text-zinc-700 text-sm font-medium transition-colors"
              >
                Skip for now
              </button>
            </div>
          </div>
        )}

        {/* Step 7: Instant Estimate Display */}
        {step === 7 && (
          <div className="space-y-4" data-testid="step-estimate">
            {/* Promotion Banner */}
            {promotion && (
              <div className="bg-gradient-to-r from-amber-500 to-orange-500 rounded-xl p-4 text-white text-center">
                <p className="font-bold text-lg">{promotion.name}</p>
                <p className="text-sm opacity-90">
                  Save {promotion.discount_type === "percent" ? `${promotion.discount_value}%` : `$${promotion.discount_value.toFixed(2)}`} on your service
                </p>
                {promotion.description && (
                  <p className="text-xs opacity-80 mt-1">{promotion.description}</p>
                )}
                {/* Countdown timer */}
                {promoCountdown && (
                  <div className="mt-2 pt-2 border-t border-white/30">
                    <p className="text-xs opacity-75 uppercase tracking-wide mb-1">Offer ends in</p>
                    <p className="text-lg font-bold font-mono">
                      {promoCountdown.days > 0 && <span>{promoCountdown.days}d </span>}
                      {String(promoCountdown.hours).padStart(2, '0')}:{String(promoCountdown.minutes).padStart(2, '0')}:{String(promoCountdown.seconds).padStart(2, '0')}
                    </p>
                  </div>
                )}
              </div>
            )}

            <div className="text-center">
              <div className="w-12 h-12 bg-green-100 rounded-full flex items-center justify-center mx-auto mb-2">
                <Calculator className="w-6 h-6 text-[#207024]" />
              </div>
              <h1 className="text-xl font-bold text-zinc-900">Your Instant Quote</h1>
              <p className="text-zinc-500 text-sm">Hi {formData.firstName}! Here's your quote:</p>
            </div>

            {/* Memorial Day Sale Applied — banner driven by either active
                campaign mode (new lead) or saved estimate_data (resume) */}
            {(() => {
              const ed = resumeData?.estimate_data || estimate || {};
              const isMD = ed.campaign === MEMORIAL_DAY_CAMPAIGN
                || (isMemorialDayActive && ((parseInt(formData.windowRescreens) || 0) + (parseInt(formData.windowNewBuilds) || 0) + (parseInt(formData.windowNotSure) || 0)) >= 10);
              if (!isMD) return null;
              const tierNum = ed.memorial_day_tier
                || (isMemorialDayActive ? getMemorialDayTier((parseInt(formData.windowRescreens) || 0) + (parseInt(formData.windowNewBuilds) || 0) + (parseInt(formData.windowNotSure) || 0)).tier : 0);
              let qualifyText = "";
              if (tierNum === 3) qualifyText = "FREE Sliding Screen Door ($426 Value)";
              else if (tierNum === 2) qualifyText = "$200 OFF";
              else if (tierNum === 1) qualifyText = "$100 OFF";
              if (!qualifyText) return null;
              return (
                <div
                  data-testid="memorial-applied-banner"
                  className="rounded-xl border-2 border-[#b22234] bg-gradient-to-r from-red-50 via-white to-blue-50 p-3 text-center"
                >
                  <div className="text-xs font-bold uppercase tracking-wide text-[#b22234]">
                    🇺🇸 Memorial Day Sale Applied
                  </div>
                  <p className="mt-1 text-sm font-semibold text-zinc-800">
                    You currently qualify for: <span className="text-[#207024]">{qualifyText}</span>
                  </p>
                </div>
              );
            })()}
            
            {/* Use resumeData.estimate_data if available (for resume flow), otherwise use calculated estimate */}
            {(() => {
              const displayEstimate = resumeData?.estimate_data || estimate;
              const rawDisplayItems = resumeData?.estimate_data 
                ? (resumeData.estimate_data.line_items || []).map(item => ({
                    label: `${item.name} (${item.quantity})`,
                    amount: item.line_total,
                    taxable: item.taxable,
                    unit_price: item.unit_price,
                    quantity: item.quantity,
                    upgrade_unit_price: item.upgrade_unit_price
                  }))
                : estimate.items;
              const displayTax = displayEstimate.tax || 0;
              const baseTotal = displayEstimate.total || 0;

              // ---- Memorial Day promo detection (auto-applied tier-based) ----
              // For Memorial Day, the estimate carries:
              //   - discount_amount > 0 for tier 1/2 ($100/$200 OFF)
              //   - memorial_day_free_door = true for tier 3 (FREE Sliding Door, $0 line item)
              //   - subtotal = pre-discount subtotal
              const mdTier = Number(displayEstimate.memorial_day_tier) || 0;
              const mdFreeDoorFlag = !!displayEstimate.memorial_day_free_door;
              const mdDiscountAmt = mdTier > 0 ? (Number(displayEstimate.discount_amount) || 0) : 0;
              // Identify the FREE Sliding Screen Door line so we can render it
              // specially (multi-line, mobile-friendly) instead of crammed into
              // the regular line-item row.
              const mdFreeDoorItem = rawDisplayItems.find(it =>
                /Memorial Day Sale/i.test(it.label || "")
              );
              const isMDPromo = mdTier > 0 || mdDiscountAmt > 0 || mdFreeDoorFlag || !!mdFreeDoorItem;
              const displayItems = isMDPromo
                ? rawDisplayItems.filter(it => !/Memorial Day Sale/i.test(it.label || ""))
                : rawDisplayItems;

              // Per-lead discount stored on the saved estimate (admin-applied
              // from the Estimates portal). Separate from the site-wide
              // `promotion` path below. NOTE: Memorial Day uses the same
              // `discount_*` fields but is identified by `memorial_day_tier`,
              // so we exclude it here to avoid double-rendering.
              const leadDiscountType = !isMDPromo ? displayEstimate.discount_type : null;
              const leadDiscountValue = Number(displayEstimate.discount_value) || 0;
              const leadSubtotal = Number(displayEstimate.subtotal) || 0;
              // Derive the dollar amount when the discount is a percent so
              // corrupt/stale stored `discount_amount` values (e.g. legacy
              // rows where the admin edit saved a mismatched number) can't
              // contradict the "10%" label the customer sees.
              let leadDiscountAmount = 0;
              if (!isMDPromo) {
                if (leadDiscountType === "percent" && leadDiscountValue > 0) {
                  leadDiscountAmount = Math.round(leadSubtotal * (leadDiscountValue / 100) * 100) / 100;
                } else {
                  leadDiscountAmount = Number(displayEstimate.discount_amount) || 0;
                }
              }
              const hasLeadDiscount = !!leadDiscountType && leadDiscountAmount > 0;
              // Rewrite baseTotal to reflect the lead discount so the big
              // "Quote Total" (and the strikethrough) match the discount
              // row above them.
              let baseTotalForDisplay = baseTotal;
              if (hasLeadDiscount) {
                baseTotalForDisplay = Math.max(
                  0,
                  leadSubtotal - leadDiscountAmount + displayTax,
                );
              }
              const leadDiscountLabel = leadDiscountType === "percent"
                ? `${leadDiscountValue}% off`
                : "Discount";

              // Calculate discount if promotion is active
              let discountAmount = 0;
              let finalTotal = baseTotalForDisplay;
              if (promotion) {
                if (promotion.discount_type === "percent") {
                  discountAmount = baseTotalForDisplay * (promotion.discount_value / 100);
                } else {
                  discountAmount = promotion.discount_value;
                }
                finalTotal = Math.max(0, baseTotalForDisplay - discountAmount);
              }

              // Before-discount total used when the Memorial Day promo applies.
              // baseTotal already nets out mdDiscountAmt, so we add it back.
              const mdBeforeDiscountTotal = baseTotal + mdDiscountAmt;
              // Tier 3 (FREE Sliding Screen Door) is a value-add, not a cash
              // discount — `total` is unchanged so we render it differently
              // (no strikethrough, "value added" copy, no "-$426" line).
              const mdIsFreeDoor = isMDPromo && (mdFreeDoorFlag || !!mdFreeDoorItem) && mdDiscountAmt === 0;
              const mdIsCashDiscount = isMDPromo && mdDiscountAmt > 0;
              // For tier 3, "before discount total" = current total + a flat
              // $426 (the door's all-in value). Savings displayed = $426 flat.
              const FREE_DOOR_VALUE = 426;
              const mdFreeDoorBeforeTotal = mdIsFreeDoor
                ? baseTotal + FREE_DOOR_VALUE
                : 0;
              const mdFreeDoorSavings = FREE_DOOR_VALUE;
              
              return (
                <>
                  <div className="bg-white rounded-xl border-2 border-zinc-200 overflow-hidden">
                    <div className="p-3 space-y-2">
                      {displayItems.map((item, idx) => (
                        <div key={idx} className="flex justify-between items-center text-sm">
                          <div className="text-zinc-700">
                            <span>{item.label}</span>
                            {item.quantity > 1 && item.unit_price > 0 && (
                              <span className="text-zinc-400 text-xs ml-1">${item.unit_price.toFixed(2)}/ea</span>
                            )}
                            {meshKindFromName(item.label) && <MeshPhoto kind={meshKindFromName(item.label)} compact />}
                            {item.upgrade_unit_price > 0 && (
                              <p className="text-xs text-amber-800 bg-amber-50 rounded-md px-2 py-1 mt-1 leading-snug" data-testid={`frame-check-note-${idx}`}>
                                Rescreen: ${Number(item.unit_price).toFixed(2)} each. If a frame is bent or broken, a new frame is ${Number(item.upgrade_unit_price).toFixed(2)} each. We'll confirm with you before doing any work.
                              </p>
                            )}
                          </div>
                          <span className="font-semibold">
                            ${item.amount.toFixed(2)}
                            {item.taxable && <span className="text-xs text-zinc-400 ml-1">+tax</span>}
                          </span>
                        </div>
                      ))}
                    </div>
                    
                    {displayTax > 0 && (
                      <div className="px-3 py-2 bg-zinc-50 border-t flex justify-between text-sm">
                        <span className="text-zinc-600">Tax (8.75%)</span>
                        <span className="font-semibold">${displayTax.toFixed(2)}</span>
                      </div>
                    )}

                    {/* ---- Memorial Day Sale — Before/After breakdown ---- */}
                    {isMDPromo && (
                      <>
                        {/* Before Discount Total — shown for all MD tiers.
                            Tier 1/2: subtotal+tax before the cash discount.
                            Tier 3: shows what total would have been if we'd
                            charged for the sliding door (+$426 flat). */}
                        {(mdIsCashDiscount || mdIsFreeDoor) && (
                          <div
                            className="px-3 py-2 bg-zinc-50 border-t flex justify-between items-center text-sm"
                            data-testid="md-before-discount-total"
                          >
                            <span className="text-zinc-600">Before Discount Total</span>
                            <span className="font-semibold text-zinc-500 line-through">
                              ${(mdIsFreeDoor ? mdFreeDoorBeforeTotal : mdBeforeDiscountTotal).toFixed(2)}
                            </span>
                          </div>
                        )}

                        {/* Memorial Day savings — multi-line on mobile so the
                            FREE Sliding Screen Door copy never crams together. */}
                        <div
                          className="px-3 py-2 bg-gradient-to-r from-red-50 via-white to-blue-50 border-t border-[#b22234]/30"
                          data-testid="md-savings-row"
                        >
                          <div className="flex items-start justify-between gap-3">
                            <div className="text-[#b22234] font-bold text-sm leading-snug">
                              <div className="text-[10px] uppercase tracking-wide text-[#b22234]/80">
                                🇺🇸 Memorial Day Sale
                              </div>
                              {mdIsFreeDoor ? (
                                <div className="text-[#207024]">
                                  <div>FREE Sliding Screen Door</div>
                                  <div className="text-xs font-semibold text-[#207024]/80">
                                    $426 value — added to your order
                                  </div>
                                </div>
                              ) : (
                                <div className="text-[#207024]">
                                  {mdTier === 2 ? "$200 OFF" : "$100 OFF"}
                                  <span className="ml-1 text-xs font-semibold text-[#207024]/80">
                                    applied
                                  </span>
                                </div>
                              )}
                            </div>
                            <span
                              className="font-bold text-[#207024] text-sm whitespace-nowrap"
                              data-testid="md-savings-amount"
                            >
                              {mdIsFreeDoor
                                ? `-$${mdFreeDoorSavings.toFixed(2)}`
                                : `-$${mdDiscountAmt.toFixed(2)}`}
                            </span>
                          </div>
                        </div>
                      </>
                    )}

                    {/* Per-lead discount applied by admin (e.g., 10% off) */}
                    {hasLeadDiscount && (
                      <div className="px-3 py-2 bg-green-50 border-t flex justify-between text-sm text-green-700" data-testid="lead-discount-row">
                        <span className="font-semibold">💰 {leadDiscountLabel}</span>
                        <span className="font-semibold">-${leadDiscountAmount.toFixed(2)}</span>
                      </div>
                    )}

                    {/* Discount line if promotion active */}
                    {promotion && discountAmount > 0 && (
                      <div className="px-3 py-2 bg-green-50 border-t flex justify-between text-sm text-green-700">
                        <span>{promotion.name}</span>
                        <span className="font-semibold">-${discountAmount.toFixed(2)}</span>
                      </div>
                    )}

                    {/* Price Reassurance — locked-in, no surprise charges.
                        Placed between the line-item block and the Quote
                        Total so the reassurance is read RIGHT before the
                        customer sees the number. Slightly more prominent
                        than the old muted line but still doesn't compete
                        with the total. */}
                    <div className="px-3 py-2 border-t bg-[#207024]/5">
                      <p className="text-sm font-semibold text-[#207024] text-center">
                        ✓ Your quote is locked in — no surprise charges
                      </p>
                    </div>

                    <div className="px-4 py-3 bg-[#207024] text-white flex justify-between items-center">
                      <span className="font-bold">
                        {mdIsCashDiscount || mdIsFreeDoor ? "Total After Discount" : "Quote Total"}
                      </span>
                      <div className="text-right">
                        {promotion && discountAmount > 0 && (
                          <span className="text-sm line-through opacity-70 mr-2">${baseTotal.toFixed(2)}</span>
                        )}
                        {hasLeadDiscount && !(promotion && discountAmount > 0) && leadSubtotal > 0 && (
                          <span className="text-sm line-through opacity-70 mr-2" data-testid="lead-discount-was">
                            ${(leadSubtotal + displayTax).toFixed(2)}
                          </span>
                        )}
                        {(mdIsCashDiscount || mdIsFreeDoor) && (
                          <span
                            className="text-sm line-through opacity-70 mr-2"
                            data-testid="md-total-was"
                          >
                            ${(mdIsFreeDoor ? mdFreeDoorBeforeTotal : mdBeforeDiscountTotal).toFixed(2)}
                          </span>
                        )}
                        <span className="font-bold text-xl" data-testid="md-total-after">
                          ${finalTotal.toFixed(2)}
                        </span>
                      </div>
                    </div>

                    {/* Savings reinforcement strip — only when MD promo is active */}
                    {isMDPromo && (mdIsCashDiscount || mdIsFreeDoor) && (
                      <div
                        className="px-3 py-2 bg-[#207024]/10 border-t border-[#207024]/20 text-center"
                        data-testid="md-savings-callout"
                      >
                        <p className="text-xs font-bold text-[#207024]">
                          {mdIsFreeDoor
                            ? `🎉 You're saving $${mdFreeDoorSavings.toFixed(2)} with the FREE Sliding Screen Door`
                            : `🎉 You're saving $${mdDiscountAmt.toFixed(2)} with the Memorial Day Sale`}
                        </p>
                      </div>
                    )}
                  </div>

                  <PriceGuarantee testid="price-guarantee-quote" />

                  {/* Savings reinforcement */}
                  {promotion && discountAmount > 0 && (
                    <p className="text-sm text-green-600 text-center font-medium">
                      You're saving ${discountAmount.toFixed(2)} with {promotion.name}!
                    </p>
                  )}

                  {/* Value-based trust line */}
                  <p className="text-xs text-zinc-600 text-center tracking-wide" data-testid="trust-line">
                    ✓ We come to you  •  ✓ Family owned  •  ★ 290+ 5-star reviews
                  </p>

                  <div className="space-y-3 pt-3">
                    {viewOnlyMode ? (
                      // View-only quote: no booking button. Admin will book
                      // manually and reach out to schedule.
                      <div
                        className="rounded-xl border-2 border-[#207024] bg-green-50 p-4 text-center"
                        data-testid="view-only-quote-notice"
                      >
                        <p className="font-semibold text-[#207024]">
                          Your estimate is ready
                        </p>
                        <p className="text-sm text-zinc-600 mt-1">
                          We&apos;ll reach out shortly to schedule your appointment.
                          Have a question in the meantime? Just text us back.
                        </p>
                      </div>
                    ) : (
                      <div>
                        <button
                          onClick={handleAcceptEstimate}
                          className="w-full py-3.5 bg-[#F26C2F] text-white rounded-xl font-bold text-lg hover:bg-[#D9591F] transition-colors"
                          data-testid="see-available-times-btn"
                        >
                          See Available Times →
                        </button>
                        <p className="text-xs text-zinc-500 text-center mt-2">
                          No payment required • Takes less than a minute
                        </p>

                        {/* What happens next — compact 3-step reassurance.
                            Row on wider screens, stacked on mobile. Keeps
                            scroll short: no card chrome, subtle numeric
                            badges, one-line copy per step. */}
                        <div className="mt-6" data-testid="what-happens-next">
                          <p className="text-sm font-bold text-zinc-900 text-center mb-3">
                            What happens next?
                          </p>
                          <div className="grid grid-cols-1 sm:grid-cols-3 gap-3">
                            {[
                              { n: 1, t: "Choose your appointment", d: "Pick an available day and time." },
                              { n: 2, t: "We come to you", d: "Your ScreenzZ tech completes the work at your home." },
                              { n: 3, t: "Pay after the work is complete", d: "No payment required to schedule." },
                            ].map((step) => (
                              <div key={step.n} className="flex sm:flex-col sm:items-center gap-2 sm:gap-1 sm:text-center">
                                <div className="flex-shrink-0 w-7 h-7 rounded-full bg-[#207024] text-white text-xs font-bold flex items-center justify-center">
                                  {step.n}
                                </div>
                                <div className="min-w-0">
                                  <p className="text-sm font-semibold text-zinc-900 leading-tight">{step.t}</p>
                                  <p className="text-xs text-zinc-500 leading-snug mt-0.5">{step.d}</p>
                                </div>
                              </div>
                            ))}
                          </div>
                        </div>

                        {/* Compact social-proof review under the "what next"
                            steps. Center-aligned, no card/border to stay
                            native to the page. Stars are gold (#F5A623). */}
                        <div className="text-center mt-6 space-y-1">
                          <div
                            className="text-[#F5A623] tracking-wide text-sm"
                            aria-label="5 out of 5 stars"
                            data-testid="review-stars"
                          >
                            ★★★★★
                          </div>
                          <p className="text-sm text-zinc-700 italic">
                            &ldquo;Easy to work with, great service! Super easy and cost effective!&rdquo;
                          </p>
                          <p className="text-xs text-zinc-400">
                            — John B., Google Review
                          </p>
                        </div>

                        {/* Secondary "text us" fallback for hesitant leads.
                            Uses the sms: scheme with the business Twilio
                            number fetched from /api/public-config so the
                            number stays in one place (env var). */}
                        {businessPhone && (
                          <p className="text-center text-sm text-zinc-500 mt-6" data-testid="text-us-fallback">
                            Have a question before booking?{" "}
                            <a
                              href={`sms:${businessPhone.startsWith("+") ? businessPhone : `+1${(businessPhone.match(/\d/g) || []).join("")}`}?body=Question about my ScreenzZ quote`}
                              className="text-[#207024] font-semibold underline"
                              data-testid="text-us-link"
                            >
                              Text us
                            </a>
                          </p>
                        )}
                      </div>
                    )}
                  </div>
                </>
              );
            })()}
          </div>
        )}

        {/* Step 10: V3 Whole-Home Screen Check question ------------------
            While the tech is at the home, does the customer want a broader
            look at their other screens? Kept intentionally friendly — no
            upsell pressure, no pricing shown. This step only renders when
            the V3 funnel is enabled. */}
        {step === 10 && (
          <div className="space-y-5" data-testid="step-screen-check">
            <div className="text-center space-y-3">
              {/* FREE • BONUS pill — reframes the step as a gift, not
                  an upsell question. Removes cost anxiety before the
                  customer even reads the options. */}
              <span
                className="inline-flex items-center gap-1.5 px-3 py-1 rounded-full bg-[#207024] text-white text-xs font-bold uppercase tracking-wider shadow-sm"
                data-testid="screen-check-free-badge"
              >
                <Gift className="w-3.5 h-3.5" />
                Free · Bonus
              </span>
              <h2 className="font-barlow text-2xl sm:text-3xl font-bold text-zinc-950 leading-tight">
                A free bonus while we&apos;re there
              </h2>
              <p className="text-sm text-zinc-600 max-w-md mx-auto">
                As a thank you for booking, we&apos;ll take a look at the rest of your
                screens on the house — no charge, no pressure. Just so you know if
                anything else needs attention down the road.
              </p>
            </div>

            <div className="space-y-3 pt-2">
              <button
                type="button"
                data-testid="screen-check-whole-home"
                onClick={() => handleScreenCheckAnswer("whole_home_check")}
                className="w-full text-left p-4 rounded-xl border-2 border-zinc-200 hover:border-[#207024] hover:bg-green-50 transition-colors group"
              >
                <div className="flex items-center gap-2 flex-wrap">
                  <span className="font-semibold text-zinc-900 group-hover:text-[#207024]">
                    Yes, check them all
                  </span>
                  <span className="text-[10px] font-bold uppercase tracking-wider px-1.5 py-0.5 rounded bg-green-100 text-[#207024]">
                    Free
                  </span>
                </div>
                <div className="text-sm text-zinc-500 mt-0.5">
                  Look at every window and door screen while you&apos;re there.
                </div>
              </button>

              <button
                type="button"
                data-testid="screen-check-quick"
                onClick={() => handleScreenCheckAnswer("quick_check")}
                className="w-full text-left p-4 rounded-xl border-2 border-zinc-200 hover:border-[#207024] hover:bg-green-50 transition-colors group"
              >
                <div className="flex items-center gap-2 flex-wrap">
                  <span className="font-semibold text-zinc-900 group-hover:text-[#207024]">
                    Yes, take a quick look
                  </span>
                  <span className="text-[10px] font-bold uppercase tracking-wider px-1.5 py-0.5 rounded bg-green-100 text-[#207024]">
                    Free
                  </span>
                </div>
                <div className="text-sm text-zinc-500 mt-0.5">
                  Just flag anything obvious that could use attention.
                </div>
              </button>

              <button
                type="button"
                data-testid="screen-check-none"
                onClick={() => handleScreenCheckAnswer("selected_only")}
                className="w-full text-left p-4 rounded-xl border-2 border-zinc-200 hover:border-zinc-400 transition-colors"
              >
                <div className="font-semibold text-zinc-900">No thanks</div>
                <div className="text-sm text-zinc-500 mt-0.5">
                  Just the screens I selected.
                </div>
              </button>
            </div>

            <button
              type="button"
              onClick={handleBack}
              data-testid="screen-check-back"
              className="w-full py-2 text-sm text-zinc-500 hover:text-zinc-700 transition-colors"
            >
              ← Back to my estimate
            </button>
          </div>
        )}

        {/* Step 8: Booking — below-minimum notice with two friendly paths */}
        {step === 8 && (() => {
          // Use the SAME granular counts the estimate line items are
          // built from — NOT the top-of-flow "totalWindowScreens" /
          // "totalSliders" inputs (which can stay 0 if the user filled
          // granular fields directly). Previously the check only looked
          // at those top counters, so a customer with e.g. 1 new window
          // screen slipped past the 3-screen minimum → they'd book at
          // $76 and we'd waste a truck roll.
          const windowScreens =
            (parseInt(formData.windowRescreens) || 0) +
            (parseInt(formData.windowNewBuilds) || 0) +
            (parseInt(formData.windowNotSure) || 0);
          const sliderScreens =
            (parseInt(formData.sliderRescreens) || 0) +
            (parseInt(formData.sliderNewBuilds) || 0) +
            (parseInt(formData.sliderNotSure) || 0);
          const petScreens = formData.wantsPetScreen ? (parseInt(formData.petScreenCount) || 0) : 0;
          const solarScreens = formData.wantsSolarScreen ? (parseInt(formData.solarScreenCount) || 0) : 0;
          const totalScreens = windowScreens + sliderScreens + petScreens + solarScreens;
          // Below-minimum if the customer has fewer than 3 total screens
          // AND no full sliding screen door (which auto-meets the 3-screen
          // mobile minimum). Any total ≥ 3 clears the minimum.
          return totalScreens > 0 && totalScreens < 3 && sliderScreens === 0;
        })() && (
          <div className="space-y-6" data-testid="step-booking-below-minimum">
            <div>
              <h1 className="text-2xl font-bold text-zinc-900">A couple of other options</h1>
              <p className="text-zinc-500 mt-1">
                Our mobile service has a 3-screen minimum, but you&apos;ve still got two simple ways to get your screens taken care of.
              </p>
            </div>

            {/* Path 1 — mail-in new build (provide measurements for new screens) */}
            <button
              type="button"
              data-testid="below-min-path-mailin"
              onClick={async () => {
                try {
                  if (formData.leadId) {
                    await axios.put(`${API}/leads/${formData.leadId}/flow-choice`, { choice: "mail_in_new_build" });
                  }
                } catch (err) {
                  console.warn("flow-choice tag failed:", err);
                }
                window.location.href = "sms:8555004416?body=Hi, I'd like to provide measurements for new screens.";
              }}
              className="w-full text-left rounded-2xl border-2 border-zinc-200 hover:border-[#207024] bg-white p-5 transition-colors"
            >
              <div className="flex items-start gap-4">
                <div className="shrink-0 w-12 h-12 rounded-xl bg-green-50 flex items-center justify-center">
                  <svg className="w-6 h-6 text-[#207024]" fill="none" stroke="currentColor" strokeWidth="2" viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" d="M9 5H7a2 2 0 00-2 2v12a2 2 0 002 2h10a2 2 0 002-2V7a2 2 0 00-2-2h-2M9 5a2 2 0 002 2h2a2 2 0 002-2M9 5a2 2 0 012-2h2a2 2 0 012 2m-6 9l2 2 4-4" />
                  </svg>
                </div>
                <div className="flex-1 min-w-0">
                  <p className="font-semibold text-zinc-900">Provide measurements for new screens</p>
                  <p className="text-sm text-zinc-500 mt-1">Need new builds? Send us your dimensions and we&apos;ll build them in shop &amp; notify you when they&apos;re ready for pickup.</p>
                  <p className="text-xs font-semibold text-[#207024] mt-2">Text us to share measurements →</p>
                </div>
              </div>
            </button>

            {/* Path 2 — drop off rescreen */}
            <button
              type="button"
              data-testid="below-min-path-dropoff"
              onClick={async () => {
                try {
                  if (formData.leadId) {
                    await axios.put(`${API}/leads/${formData.leadId}/flow-choice`, { choice: "drop_off_rescreen" });
                  }
                } catch (err) {
                  console.warn("flow-choice tag failed:", err);
                }
                window.location.href = "sms:8555004416?body=Hi, I'd like to schedule a drop-off for screen rescreening.";
              }}
              className="w-full text-left rounded-2xl border-2 border-zinc-200 hover:border-[#207024] bg-white p-5 transition-colors"
            >
              <div className="flex items-start gap-4">
                <div className="shrink-0 w-12 h-12 rounded-xl bg-green-50 flex items-center justify-center">
                  <svg className="w-6 h-6 text-[#207024]" fill="none" stroke="currentColor" strokeWidth="2" viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" d="M3 7l9-4 9 4M3 7v10l9 4 9-4V7M3 7l9 4m0 0l9-4m-9 4v10" />
                  </svg>
                </div>
                <div className="flex-1 min-w-0">
                  <p className="font-semibold text-zinc-900">Drop off your screens</p>
                  <p className="text-sm text-zinc-500 mt-1">Bring your existing screens to our shop for rescreening and pick them up when they&apos;re ready.</p>
                  <p className="text-xs font-semibold text-[#207024] mt-2">Text us to schedule drop-off &amp; pick-up →</p>
                </div>
              </div>
            </button>

            <button
              onClick={() => setStep(step - 1)}
              data-testid="below-minimum-back-btn"
              className="w-full py-4 rounded-xl font-semibold bg-zinc-100 text-zinc-700 hover:bg-zinc-200 transition-colors flex items-center justify-center gap-2"
            >
              <ArrowLeft className="w-5 h-5" />
              Back
            </button>
          </div>
        )}

        {/* Step 8: Booking */}
        {step === 8 && !(() => {
          // Mirror the below-minimum predicate above so the two panels
          // are exact inverses and no customer can slip past.
          const windowScreens =
            (parseInt(formData.windowRescreens) || 0) +
            (parseInt(formData.windowNewBuilds) || 0) +
            (parseInt(formData.windowNotSure) || 0);
          const sliderScreens =
            (parseInt(formData.sliderRescreens) || 0) +
            (parseInt(formData.sliderNewBuilds) || 0) +
            (parseInt(formData.sliderNotSure) || 0);
          const petScreens = formData.wantsPetScreen ? (parseInt(formData.petScreenCount) || 0) : 0;
          const solarScreens = formData.wantsSolarScreen ? (parseInt(formData.solarScreenCount) || 0) : 0;
          const totalScreens = windowScreens + sliderScreens + petScreens + solarScreens;
          return totalScreens > 0 && totalScreens < 3 && sliderScreens === 0;
        })() && (
          <div className="space-y-4" data-testid="step-booking">
            {/* Headline — matches the previous-page CTA promise "See
                Available Times". No re-introduction, no re-form-fill —
                the customer already gave us name/phone/email/address on
                earlier steps. */}
            <div>
              <h1 className="text-2xl font-bold text-zinc-900">Choose a time that works for you</h1>
              <p className="text-zinc-500 mt-1 text-sm">Select an available appointment below.</p>
            </div>

            {/* Compact quote reminder — reassures they haven't lost their
                price without pushing the actual scheduling below the fold.
                "View details" toggles a small line-item recap inline; we
                don't yank them back to the quote step. */}
            {(() => {
              const bookingTotal = (() => {
                const est = resumeData?.estimate_data || null;
                const t = est?.total ?? formData?.estimateData?.total ?? null;
                return typeof t === "number" ? t : null;
              })();
              const lineItems =
                resumeData?.estimate_data?.line_items ||
                formData?.estimateData?.line_items ||
                [];
              if (bookingTotal === null) return null;
              return (
                <div
                  className="rounded-xl border border-[#207024]/20 bg-[#207024]/5 px-3 py-2.5"
                  data-testid="booking-quote-reminder"
                >
                  <div className="flex items-center justify-between gap-2">
                    <div className="flex items-center gap-2 min-w-0">
                      <span className="text-sm font-semibold text-zinc-900">
                        Your quote: ${bookingTotal.toFixed(2)}
                      </span>
                      <span className="text-[#207024] font-bold">✓</span>
                    </div>
                    {lineItems.length > 0 && (
                      <button
                        type="button"
                        onClick={() => setBookingShowQuoteDetails((v) => !v)}
                        className="text-xs font-semibold text-[#207024] underline underline-offset-2 flex-shrink-0"
                        data-testid="booking-quote-toggle"
                      >
                        {bookingShowQuoteDetails ? "Hide" : "View details"}
                      </button>
                    )}
                  </div>
                  {bookingShowQuoteDetails && lineItems.length > 0 && (
                    <div className="mt-2 pt-2 border-t border-[#207024]/15 space-y-1">
                      {lineItems.map((li, idx) => (
                        <div key={idx} className="flex justify-between text-xs text-zinc-600">
                          <span className="truncate">
                            {li.name}
                            {li.quantity > 1 && <span className="text-zinc-400"> ×{li.quantity}</span>}
                          </span>
                          <span className="text-zinc-700 font-medium flex-shrink-0 ml-2">
                            ${(li.line_total ?? li.total ?? (li.unit_price || 0) * (li.quantity || 1)).toFixed(2)}
                          </span>
                        </div>
                      ))}
                    </div>
                  )}
                </div>
              );
            })()}

            {/* Service Address — collapsed by default when we already have
                one on file. Tapping "Change" reveals the existing
                autocomplete input and ZIP fallback with zero logic changes.
                Preserves service-area / autocomplete / ZIP validation
                downstream. */}
            {(() => {
              const hasAddress =
                (formData.address && formData.address.toLowerCase() !== "not provided yet") ||
                formData.preEstimateAddress;
              const showEditor = bookingAddressEdit || !hasAddress;
              const displayAddress =
                (formData.address && formData.address.toLowerCase() !== "not provided yet"
                  ? formData.address
                  : "") ||
                formData.preEstimateAddress ||
                "";
              if (!showEditor) {
                return (
                  <div
                    className="rounded-xl border border-zinc-200 bg-white px-3 py-2.5 flex items-start justify-between gap-3"
                    data-testid="booking-address-compact"
                  >
                    <div className="flex items-start gap-2 min-w-0 flex-1">
                      <MapPin className="w-4 h-4 mt-0.5 flex-shrink-0 text-[#207024]" />
                      <div className="min-w-0">
                        <div className="text-[11px] font-semibold text-zinc-500 uppercase tracking-wide">
                          Service Address
                        </div>
                        <div className="text-sm text-zinc-900 truncate">{displayAddress}</div>
                      </div>
                    </div>
                    <button
                      type="button"
                      onClick={() => setBookingAddressEdit(true)}
                      className="text-xs font-semibold text-[#207024] underline underline-offset-2 flex-shrink-0"
                      data-testid="booking-address-change-btn"
                    >
                      Change
                    </button>
                  </div>
                );
              }
              // Full editor — preserved verbatim from the original booking
              // step (autocomplete, zip fallback banner, ZIP input) so no
              // service-area / availability guardrail regresses.
              return (
                <div className="space-y-3">
                  <div>
                    <label className="block font-semibold text-zinc-700 mb-2 flex items-center gap-2">
                      <MapPin className="w-4 h-4" />
                      Service Address
                      {googleMapsLoaded && (
                        <span className="text-xs text-green-600">Autocomplete enabled</span>
                      )}
                    </label>
                    {resumeData && !formData.address && !formData.preEstimateAddress && (
                      <div
                        className="mb-2 flex items-start gap-2 rounded-lg border border-amber-200 bg-amber-50 px-3 py-2 text-xs text-amber-900"
                        data-testid="booking-need-address-banner"
                      >
                        <MapPin className="w-4 h-4 mt-0.5 shrink-0 text-amber-600" />
                        <span>
                          We need your full address to confirm we serve your area —
                          start typing and we'll auto-fill the rest.
                        </span>
                      </div>
                    )}
                    <input
                      ref={addressInputRef}
                      type="text"
                      value={formData.address ?? ""}
                      onChange={(e) => setFormData({ ...formData, address: e.target.value })}
                      onFocus={(e) => {
                        if (
                          formData.address === "Not provided yet" ||
                          formData.address === "not provided yet"
                        ) {
                          setFormData({ ...formData, address: "" });
                        }
                      }}
                      placeholder="Enter your address..."
                      className="w-full h-14 rounded-xl border-2 border-zinc-200 px-4 text-lg focus:border-[#207024]"
                    />
                    {formData.preEstimateAddress && !formData.address && (
                      <p className="text-xs text-zinc-400 mt-1.5 flex items-center gap-1">
                        <span>Using your previously entered address</span>
                        <button
                          type="button"
                          onClick={() => addressInputRef.current?.focus()}
                          className="text-[#207024] hover:underline ml-1"
                        >
                          Change
                        </button>
                      </p>
                    )}
                  </div>

                  {/* ZIP fallback — only shown while the editor is open.
                      We STILL rely on this value for /available-slots-batch
                      + ZIP-routing so admins in a shrinking service area
                      keep the guardrail. */}
                  <div>
                    <label className="block font-semibold text-zinc-700 mb-2">Zip Code</label>
                    <input
                      type="text"
                      maxLength={5}
                      value={formData.zipCode ?? ""}
                      onChange={(e) => {
                        setFormData({ ...formData, zipCode: e.target.value });
                        setZipNotServiced(false);
                        setManualBookOnly(false);
                        setSlotsChecked(false);
                        setAvailableSlots([]);
                      }}
                      placeholder="Enter zip code"
                      className="w-full h-14 rounded-xl border-2 border-zinc-200 px-4 text-lg focus:border-[#207024]"
                    />
                  </div>

                  {hasAddress && (
                    <button
                      type="button"
                      onClick={() => setBookingAddressEdit(false)}
                      className="text-xs font-semibold text-zinc-500 underline underline-offset-2"
                    >
                      Done editing address
                    </button>
                  )}
                </div>
              );
            })()}

            {/* Available Slots */}
            {formData.zipCode?.length === 5 && (
              <div>
                <label className="block font-semibold text-zinc-700 mb-3 flex items-center gap-2">
                  <Calendar className="w-4 h-4" />
                  Available Times
                </label>
                
                {/* Zip code not serviced message */}
                {zipNotServiced ? (
                  <div className="bg-amber-50 border-2 border-amber-200 rounded-xl p-5 text-center" data-testid="zip-not-serviced-notice">
                    <AlertCircle className="w-10 h-10 text-amber-500 mx-auto mb-3" />
                    <p className="text-zinc-800 font-medium mb-2">
                      We don&apos;t currently service your zip code.
                    </p>
                    <p className="text-zinc-600 text-sm mb-4">
                      Text us to see if we can accommodate — we sometimes make exceptions for nearby areas.
                    </p>
                    <a
                      href="sms:8555004416"
                      data-testid="zip-not-serviced-text-us-btn"
                      className="inline-flex items-center gap-2 bg-[#F26C2F] text-white px-6 py-3 rounded-xl font-semibold hover:bg-[#D9591F] transition-colors"
                    >
                      <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M8 12h.01M12 12h.01M16 12h.01M21 12c0 4.418-4.03 8-9 8a9.863 9.863 0 01-4.255-.949L3 20l1.395-3.72C3.512 15.042 3 13.574 3 12c0-4.418 4.03-8 9-8s9 3.582 9 8z" />
                      </svg>
                      Text (855) 500-4416
                    </a>
                  </div>
                ) : manualBookOnly ? (
                  /* Self-book disabled for this ZIP — admin wants to
                     handle scheduling manually. Prompt customer to text
                     us instead of showing a times panel. */
                  (() => {
                    const leadRef = (leadId || resumeData?.lead_id || "").toString();
                    const shortRef = leadRef ? leadRef.replace(/^lead_/, "").slice(0, 6).toUpperCase() : "";
                    const refLabel = shortRef ? ` (Quote #${shortRef})` : "";
                    const smsBody = encodeURIComponent(
                      `Hi! I'd like to book my ScreenzZ appointment${refLabel}.`
                    );
                    return (
                      <div
                        className="bg-green-50 border-2 border-[#207024] rounded-xl p-5 text-center"
                        data-testid="manual-book-only-notice"
                      >
                        <div className="text-3xl mb-1">📞</div>
                        <p className="text-zinc-900 font-bold text-lg mb-1">
                          Ready to schedule? Text us!
                        </p>
                        <p className="text-zinc-700 text-sm mb-4">
                          Your area is currently by-request-only. Send us a text and
                          we&apos;ll get you on the calendar right away.
                        </p>
                        <a
                          href={`sms:8555004416?body=${smsBody}`}
                          data-testid="manual-book-text-us-btn"
                          className="inline-flex items-center gap-2 bg-[#207024] text-white px-6 py-3 rounded-xl font-semibold hover:bg-[#185519] transition-colors"
                        >
                          <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M8 12h.01M12 12h.01M16 12h.01M21 12c0 4.418-4.03 8-9 8a9.863 9.863 0 01-4.255-.949L3 20l1.395-3.72C3.512 15.042 3 13.574 3 12c0-4.418 4.03-8 9-8s9 3.582 9 8z" />
                          </svg>
                          Text (855) 500-4416
                        </a>
                      </div>
                    );
                  })()
                ) : availableSlots.length === 0 ? (
                  <>
                    <button
                      onClick={() => fetchAvailableSlots({ force: true })}
                      disabled={loadingSlots}
                      data-testid="check-availability-btn"
                      className="w-full py-3 bg-[#F26C2F] hover:bg-[#D9591F] rounded-xl text-white font-semibold disabled:opacity-60 disabled:cursor-wait flex items-center justify-center gap-2"
                    >
                      {loadingSlots ? (
                        <>
                          <svg className="animate-spin h-5 w-5 text-white" xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24">
                            <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4"></circle>
                            <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8v4a4 4 0 00-4 4H4z"></path>
                          </svg>
                          Checking availability…
                        </>
                      ) : (
                        "Check Availability"
                      )}
                    </button>
                    {slotsChecked && !loadingSlots && (
                      <div className="mt-4 bg-amber-50 border-2 border-amber-200 rounded-xl p-5 text-center">
                        <AlertCircle className="w-8 h-8 text-amber-500 mx-auto mb-2" />
                        <p className="text-zinc-800 font-medium mb-1">
                          No openings in the next 3 weeks.
                        </p>
                        <p className="text-zinc-600 text-sm mb-3">
                          Text or call us and we'll find a time that works.
                        </p>
                        <a
                          href="sms:8555004416"
                          className="inline-flex items-center gap-2 bg-[#F26C2F] text-white px-5 py-2.5 rounded-xl font-semibold hover:bg-[#D9591F] transition-colors"
                        >
                          Text (855) 500-4416
                        </a>
                      </div>
                    )}
                  </>
                ) : (
                  <div className="space-y-3">
                    {/* Compact horizontal date-chip strip. Every day with
                        real availability is a chip; horizontal scroll on
                        mobile. Tapping picks a date and reveals its times
                        below. If a slot is already selected on entry
                        (resume flow), we honor that date. Otherwise we
                        auto-select the first day so times appear
                        immediately without a second tap.
                        Preserved: nearby-tier logic (shown as a small pill
                        on the selected day's times header). */}
                    {(() => {
                      const derivedActiveDate =
                        bookingSelectedDate ||
                        formData.selectedSlot?.date ||
                        (availableSlots[0]?.date ?? null);
                      const activeDay =
                        availableSlots.find((d) => d.date === derivedActiveDate) ||
                        availableSlots[0];
                      const tier = (() => {
                        if (!activeDay) return null;
                        const m = activeDay.nearestJobMiles;
                        if (m === null || m === undefined || m > 10) return null;
                        if (m <= 3) return { label: "Right in your neighborhood", pill: "text-green-800 bg-green-100 border-green-300" };
                        if (m <= 6) return { label: "Very close to your area",    pill: "text-green-800 bg-green-100 border-green-300" };
                        return              { label: "Near your area",            pill: "text-emerald-700 bg-emerald-100 border-emerald-200" };
                      })();
                      // Extract "Tue 15" style short label from the full
                      // dateLabel (e.g. "Tuesday, Sep 15"). Falls back to
                      // dateLabel when the format is unexpected.
                      const shortLabel = (d) => {
                        const raw = d.dateLabel || "";
                        const m = raw.match(/^(\w{3})\w*,?\s*\w*\s*(\d+)/);
                        if (m) return { wd: m[1], num: m[2] };
                        return { wd: raw.slice(0, 3), num: "" };
                      };
                      return (
                        <>
                          <div>
                            <p className="text-sm font-semibold text-zinc-700 mb-2">Choose a date</p>
                            <div
                              className="flex gap-2 overflow-x-auto pb-1 -mx-1 px-1 snap-x"
                              data-testid="date-chip-row"
                            >
                              {availableSlots.map((day) => {
                                const isActive = activeDay?.date === day.date;
                                const s = shortLabel(day);
                                return (
                                  <button
                                    key={day.date}
                                    type="button"
                                    onClick={() => setBookingSelectedDate(day.date)}
                                    data-testid={`date-chip-${day.date}`}
                                    className={`flex-shrink-0 snap-start w-16 py-2 rounded-xl border-2 text-center transition-all ${
                                      isActive
                                        ? "bg-[#207024] text-white border-[#207024]"
                                        : "bg-white text-zinc-800 border-zinc-200 hover:border-zinc-400"
                                    }`}
                                  >
                                    <div className={`text-[11px] uppercase font-semibold ${isActive ? "opacity-90" : "text-zinc-500"}`}>
                                      {s.wd}
                                    </div>
                                    <div className="text-lg font-bold leading-none mt-0.5">{s.num}</div>
                                  </button>
                                );
                              })}
                            </div>
                          </div>

                          {activeDay && (
                            <div>
                              <div className="flex items-center justify-between mb-2 flex-wrap gap-2">
                                <p className="text-sm font-semibold text-zinc-700">
                                  Available Times — {activeDay.dateLabel}
                                </p>
                                {tier && (
                                  <span
                                    className={`inline-flex items-center gap-1 text-[11px] font-semibold border px-2 py-0.5 rounded-full ${tier.pill}`}
                                    data-testid={`nearby-badge-${activeDay.date}`}
                                    title={`${activeDay.nearestJobMiles} mi from another booked job`}
                                  >
                                    <svg className="w-3 h-3" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5">
                                      <path d="M12 2 L13.4 8.6 L20 10 L13.4 11.4 L12 18 L10.6 11.4 L4 10 L10.6 8.6 Z" strokeLinejoin="round" />
                                    </svg>
                                    {tier.label}
                                  </span>
                                )}
                              </div>
                              <div className="grid grid-cols-2 gap-2" data-testid={`times-grid-${activeDay.date}`}>
                                {activeDay.times.map((slot) => {
                                  const selected =
                                    formData.selectedSlot?.date === activeDay.date &&
                                    formData.selectedSlot?.time === slot.time;
                                  return (
                                    <button
                                      key={slot.time}
                                      type="button"
                                      onClick={() =>
                                        setFormData({
                                          ...formData,
                                          selectedSlot: {
                                            date: activeDay.date,
                                            time: slot.time,
                                            label: slot.label,
                                            tech_name: slot.tech_name,
                                            tech_id: slot.tech_id,
                                          },
                                        })
                                      }
                                      data-testid={`time-slot-${activeDay.date}-${slot.time}`}
                                      className={`py-3 rounded-lg font-semibold text-center transition-all border-2 ${
                                        selected
                                          ? "bg-[#F26C2F] text-white border-[#F26C2F]"
                                          : "bg-white text-zinc-800 border-zinc-200 hover:border-zinc-400"
                                      }`}
                                    >
                                      {selected && <span className="mr-1">✓</span>}
                                      {slot.label}
                                    </button>
                                  );
                                })}
                              </div>
                            </div>
                          )}

                          <p className="text-center text-xs text-zinc-500 pt-1">
                            Can't find a time that works?{" "}
                            <a href="sms:8555004416" className="text-[#207024] font-medium underline">
                              Text us
                            </a>
                          </p>
                        </>
                      );
                    })()}
                  </div>
                )}
              </div>
            )}

            {formData.selectedSlot && reservationTimer && (
              <div className="rounded-xl px-3 py-2 bg-amber-50 border border-amber-200 flex items-center justify-between text-sm">
                <span className="text-amber-800 font-medium">Your slot is reserved</span>
                <span className={`inline-flex items-center gap-1 font-semibold ${reservationTimer <= 30 ? "text-red-700" : "text-amber-700"}`}>
                  <Clock className="w-4 h-4" />
                  {formatTimer(reservationTimer)}
                </span>
              </div>
            )}
          </div>
        )}

        {/* Step 9: Success */}
        {step === 9 && (
          <SuccessStep
            firstName={formData.firstName}
            hasQuestion={hasQuestion}
            selectedSlot={formData.selectedSlot}
            screenCheckPreference={formData.screen_check_preference}
          />
        )}

        {/* Navigation Buttons */}
        {step < 6 && (
          <div className={`flex flex-col gap-3 ${step === 5 ? "mt-3" : "mt-8"}`}>
            <button
              onClick={handleNext}
              disabled={!canProceed() || loading}
              className={`w-full py-4 rounded-xl font-bold text-lg flex items-center justify-center gap-2 transition-all whitespace-nowrap ${
                canProceed() && !loading
                  ? "bg-[#F26C2F] text-white hover:bg-[#D9591F]"
                  : "bg-zinc-200 text-zinc-400 cursor-not-allowed"
              }`}
            >
              {loading ? (
                <div className="animate-spin rounded-full h-5 w-5 border-2 border-white border-t-transparent" />
              ) : (
                <>
                  {step === 5 ? "Get My Quote" : "Continue"}
                  <ArrowRight className="w-5 h-5" />
                </>
              )}
            </button>
            {step > 0 && (
              <button
                onClick={handleBack}
                className="w-full py-2 text-zinc-500 font-medium flex items-center justify-center gap-1 hover:text-zinc-700 transition-colors"
              >
                <ArrowLeft className="w-4 h-4" />
                Back
              </button>
            )}
          </div>
        )}

        {/* Booking Navigation */}
        {step === 8 && !(() => {
          // Mirror the below-minimum predicate above so the two panels
          // are exact inverses and no customer can slip past.
          const windowScreens =
            (parseInt(formData.windowRescreens) || 0) +
            (parseInt(formData.windowNewBuilds) || 0) +
            (parseInt(formData.windowNotSure) || 0);
          const sliderScreens =
            (parseInt(formData.sliderRescreens) || 0) +
            (parseInt(formData.sliderNewBuilds) || 0) +
            (parseInt(formData.sliderNotSure) || 0);
          const petScreens = formData.wantsPetScreen ? (parseInt(formData.petScreenCount) || 0) : 0;
          const solarScreens = formData.wantsSolarScreen ? (parseInt(formData.solarScreenCount) || 0) : 0;
          const totalScreens = windowScreens + sliderScreens + petScreens + solarScreens;
          return totalScreens > 0 && totalScreens < 3 && sliderScreens === 0;
        })() && (
          <div className="flex flex-col gap-2 mt-8">
            {/* Dynamic booking CTA — mirrors the customer's selection to
                remove the last bit of "wait what am I confirming?" doubt.
                Falls back to the original label until a slot is picked. */}
            <button
              ref={bookButtonRef}
              onClick={bookAppointment}
              disabled={!canProceed() || loading}
              data-testid="book-appointment-btn"
              className={`w-full py-4 rounded-xl font-bold text-lg flex items-center justify-center gap-2 transition-all ${
                canProceed() && !loading
                  ? "bg-[#F26C2F] text-white hover:bg-[#D9591F]"
                  : "bg-zinc-200 text-zinc-400 cursor-not-allowed"
              }`}
            >
              {loading ? (
                <div className="animate-spin rounded-full h-5 w-5 border-2 border-white border-t-transparent" />
              ) : formData.selectedSlot ? (
                <>
                  {(() => {
                    const day = availableSlots.find((d) => d.date === formData.selectedSlot.date);
                    // dateLabel usually reads "Tuesday, Sep 15" or similar; grab
                    // the weekday for the punchier CTA copy the spec asks for.
                    const weekday = (day?.dateLabel || "").split(",")[0] || day?.dateLabel || "Selected day";
                    return `Book ${weekday} at ${formData.selectedSlot.label} →`;
                  })()}
                </>
              ) : (
                <>
                  Book Appointment
                  <CheckCircle className="w-5 h-5" />
                </>
              )}
            </button>
            <p className="text-xs text-zinc-500 text-center">
              No payment required to book
            </p>
            <button
              onClick={() => setStep(7)}
              className="w-full py-2 text-zinc-500 font-medium flex items-center justify-center gap-1 hover:text-zinc-700 transition-colors text-sm"
            >
              <ArrowLeft className="w-4 h-4" />
              Back to quote
            </button>
          </div>
        )}
      </div>

      {/* Post-booking "Flexible Schedule" opt-in. Renders on the success step
          so customers see their confirmation FIRST, then this gentle prompt
          slides in on top. Yes → tags the appointment as flexible so admins
          can call/text them if an earlier slot opens. */}
      {showFlexibleModal && (
        <div
          className="fixed inset-0 z-50 flex items-end sm:items-center justify-center bg-black/50 p-4"
          data-testid="flexible-schedule-modal"
        >
          <div className="w-full max-w-md bg-white rounded-2xl shadow-2xl overflow-hidden">
            <div className="bg-[#F26C2F] text-white px-6 py-4">
              <h3 className="font-inter text-lg font-bold">
                One last thing — want an earlier slot?
              </h3>
            </div>
            <div className="px-6 py-5 space-y-3">
              <p className="text-sm text-zinc-700 leading-relaxed">
                If a closer-by job cancels or finishes early, we sometimes
                have openings before your scheduled date. Want us to reach
                out if one opens up?
              </p>
              <p className="text-xs text-zinc-500">
                You can always stick with your current appointment — this is
                just an optional heads-up.
              </p>
            </div>
            <div className="px-6 pb-6 flex gap-3">
              <button
                onClick={() => handleFlexibleResponse(false)}
                disabled={flexibleSaving}
                data-testid="flexible-schedule-no"
                className="flex-1 px-4 py-3 rounded-lg border-2 border-zinc-200 text-zinc-700 font-semibold hover:bg-zinc-50 transition-colors disabled:opacity-50"
              >
                No thanks
              </button>
              <button
                onClick={() => handleFlexibleResponse(true)}
                disabled={flexibleSaving}
                data-testid="flexible-schedule-yes"
                className="flex-1 px-4 py-3 rounded-lg bg-[#F26C2F] text-white font-semibold hover:bg-[#d95d24] transition-colors disabled:opacity-50"
              >
                Yes, contact me
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}
